import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import type { Json } from "@redotech/json/json";
import { CountryCode } from "@redotech/locale/countries";
import { RiskAssessmentResult } from "@redotech/shopify-client/admin.graphql";
import type { Equal } from "@redotech/util/equal";
import {
  arrayEqual,
  booleanEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import { NumberRange } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import type { Decimal128Extended } from "bson";
import type { Stripe } from "stripe";
import * as z from "zod/v4";
import type { AdLinkType } from "./ad-link";
import { OpenAIModel } from "./agents/providers/models/openai-models";
import { SupportAgentSkills } from "./agents/support-agents/skills";
import { SocialLoginOption } from "./auth/social-auth";
import { PaymentProviderType } from "./billing/billing-engine-types";
import { RecoverAttribution } from "./billing/recover/recover-attribution-types";
import { RecoverBillingSettings } from "./billing/recover/recover-billing-types";
import { BillingPeriod } from "./billing/service-invoice";
import { OrderTrackingInvoiceBillingSettings } from "./billing/tracking/tracking-billing-types";
import { isInvoiceBillingActive } from "./billing/tracking/tracking-billing-utils";
import type { BrandKit } from "./brand-kit";
import { CartOptimizationSettings } from "./cart/cart-optimization-settings";
import { CheckoutOptimizationSettings } from "./checkouts/checkout-optimization-settings";
import { PredictiveConversionSettings } from "./checkouts/predictive-conversion-settings";
import type { ConversationTag, ResponseLength } from "./conversation";
import type { Coverage, PaymentModel } from "./coverage";
import type { CoverageMlModelDb } from "./coverage-calculator/coverage-ml/registry";
import { UsdThousandthCents } from "./currency";
import type { AdBlock } from "./customer-accounts/ad-block";
import { PredefinedChipOptions } from "./customer-widget/customer-widget";
import type { DiscountValueType } from "./discount";
import { EmailDomainSettings } from "./email/domain-verification";
import { ExchangeOrderPrefixTypes } from "./exchange";
import { ExtendedWarrantySettings } from "./extended-warranty";
import { IFaqTheme } from "./faq/faq-body-schema";
import { HoldStatusStrategy } from "./flat-team-settings";
import { CustomsDeclaration } from "./fulfillments/customs-declaration";
import {
  Carriers,
  Service,
} from "./fulfillments/fulfillment-carriers-and-services";
import { BatchSuggestionStatusOption } from "./fulfillments/fulfillment-group-status";
import { ShippingRateTable } from "./integration/redo-in-shipping/shipping-rate-table";
import { LandedCostSettings } from "./landed-cost/landed-cost-settings";
import type { LanguageResourceOverride } from "./localization/resource";
import type { LocalizationSettings } from "./localization/settings";
import { ExternalSmsMarketingProvider } from "./marketing";
import { MarketingTeam } from "./marketing/marketing-team-zod";
import { OrderTrackingOrderPayloadProvider } from "./order-tracking/order-tracking-types";
import { ItemOrderBy, PageFormat } from "./outbound-labels";
import { ServiceLevelPriority } from "./outbound-labels/auto-merge";
import {
  AESExemptionType,
  ContentsType,
  CustomsDeclarationType,
  NonDeliveryOption,
  TaxIdUsage,
  USPSExemptionCode,
} from "./outbound-labels/customs";
import { FulfillmentGroupActions } from "./outbound-labels/fulfillment-group-actions";
import { FulfillmentGroupMergeMatcherKind } from "./outbound-labels/fulfillment-group-merge-matcher";
import { LearnedPresetKind } from "./outbound-labels/learned-preset";
import {
  PickListColumn,
  PickListFontSize,
  PickListImageSize,
} from "./outbound-labels/pick-list";
import { WeightFormat } from "./outbound-labels/util";
import { PageOrientation } from "./packing-slips/packing-slip-builder";
import { isStripePaymentMethod, type PaymentMethod } from "./payout";
import { PosSettings } from "./pos/settings";
import { PrinterOptions } from "./printnode/printer";
import { RedoInternalProducts } from "./redo-internal-products";
import { CompensationMethod, ReturnTypeEnum } from "./return";
import type { Bundle, BundleRules, Payer } from "./return-flow";
import type {
  MetafieldMatchType,
  PriceOption,
  SkuOption,
} from "./return-flow/condition";
import type { TriggerType } from "./return-flow/trigger";
import { ReturnStatus, ReturnStatusZod } from "./return-status";
import { AttachmentStrategy } from "./returns/attachment-strategy";
import { MerchantCarrierReturnLabelSettings } from "./returns/merchant-carrier-return-label-schema";
import { ShopifyIntegration } from "./shopify";
import {
  getUsagePlanPricing,
  ShopifySubscription,
} from "./shopify-subscription/shopify-subscription";
import { SaveForLaterSlideInPanelAnchorLocation } from "./slide-in-panel/slide-in-panel-anchor-location";
import {
  CheckoutUICustomizationSettings,
  Icons,
} from "./storefront/checkout-ui-customization";
import type { SupportAiBillingZod } from "./support/billing/ai-billing";
import { TextMessagingBilling } from "./support/billing/text-message-billing";
import { TeamSmsConfiguration } from "./support/sms/sms-types";
import { TeamVoiceConfiguration } from "./support/voice/voice-types";
import { type GetUser } from "./user";
import { WarrantiesSettings } from "./warranties";
import {
  ClickStandardCheckoutButton,
  InfoModal,
  InsertionPosition,
  TermsAndConditions,
} from "./widget";
import { WidgetConfig } from "./widget-schema";
import { AdvancedBinNamingSettings, BinNamingSettings } from "./wms/bin-naming";
import { BinParsingConfig } from "./wms/bin-parsing";
import { PickSessionStrategy, ScanType } from "./wms/schema/pick-session";

export interface StorefrontConfig {
  hostname: string;
  storefrontAccessToken: string;
}

export enum BARCODE_HEADER_FOOTER_OPTIONS {
  UPC = "upc",
  PRODUCT_VARIANT_NAME = "product-variant-name",
  PRODUCT_OVER_SKU = "product-over-sku",
  SKU_AND_WAREHOUSE = "sku-and-warehouse",
  NONE = "none",
}

export enum LocationCondition {
  PRODUCT_TAGS = "product_tags",
  DAMAGED = "damaged",
  NAME = "name",
  MULTIPLE_CHOICE = "multiple_choice",
  RETURN_REASON = "return_reason",
  COLLECTIONS = "collections",
  ORDER_TAGS = "order_tags",
  CUSTOMER_TAGS = "customer_tags",
  DISCOUNTS = "discounts",
  PRICE = "price",
  SKU = "sku",
  SALES_CHANNEL = "sales_channel",
  CUSTOMER_COUNTRY = "customer_country",
  CUSTOMER_ADDRESS = "customer_address",
  FINAL_SALE_RETURNS = "final_sale_returns",
  IS_REPAIR = "is_repair",
  VENDOR = "vendor",
  VARIANT_METAFIELD = "variant_metafield",
}

export enum ShippingInsurance {
  DISABLED = "disabled",
  ENABLED = "enabled",
}

export enum ExchangesSelection {
  RETURN_APP = "return_app",
  STORE_WEBSITE = "store_website",
}

export enum ShopSiteNavigationMode {
  /** New window */
  NEW_TAB = "new_tab",
  /** Same window */
  SAME_TAB = "same_tab",
}

export type CustomMetafield = {
  namespace: string;
  key: string;
  displayName?: string;
};

export enum CoverageCreditIncentiveType {
  FIXED = "fixed",
  PERCENTAGE = "percentage",
}

export interface CoverageCreditIncentive {
  enabled: boolean;
  type: CoverageCreditIncentiveType;
  fixedAmount: number;
  percentageSettings: {
    percentage: number;
    storeCreditAmountCap?: number | null;
  };
  minimumQualifyingPurchaseAmount: number;
  creditExpirationDays?: number | null;
  sendEmail: boolean;
}

export type CustomAttribute = { key: string; displayName?: string };

export type ProductCustomMetafield = {
  namespace: string;
  key: string;
  displayName?: string;
  scannable?: boolean;
};

// VariantCustomMetafield is the same as ProductCustomMetafield, just queried from the variant instead of the product
export type VariantCustomMetafield = ProductCustomMetafield;

export interface GetTeam {
  featureFlags: {
    [FeatureFlagKey.MERCHANT_ONBOARDING]: "available" | "guided" | "manual";
  };
  roles: string[];
  _id: string;
  email: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  name: string;
  updatedAt: string;
  images: string[];
  updatePermissionsLink?: string;
  platform: "shopify" | "commerce-cloud" | "commentsold" | "bigcommerce";
  shopify?: ShopifyIntegration;
  team: Team;
}

export type PackagePickupVariant = {
  variantId: string;
  percentage: number;
  // optional text to override the default text
  mainText?: string;
  subText?: string;
  pickupButtonText?: string;
  shippingButtonText?: string;
  returnInStoreText?: string;
};

export enum LabelExpirationStrategy {
  RETURN = "return",
  ORDER = "order",
  DELIVERY = "delivery",
}

export const upsellCardDefaultText = "You might also like";

export enum UpsellProductSource {
  RECOMMENDATIONS = "recommendations",
  COLLECTION = "collection",
  METAFIELD = "metafield",
  TAG = "tag",
}

export interface OneClickUpsellBilling {
  billingEnabled?: boolean;
  freeUntilDate?: string;
  freeUpsellsShownPerMonth?: number;
  pricePerUpsellShownInCents?: number;
}

export interface PriceBracket {
  value: number;
  pricePoint: number;
}
export interface PricingRuleSet {
  type: "fixed" | "percentage";
  countries: string[];
  priceBrackets: PriceBracket[];
}

export enum DynamicReturnTypes {
  ITEM_COUNT = "item-count",
  CART_VALUE = "cart-value",
}

export enum CustomerPortalVersion {
  V3_5 = "v3.5",
  V4_0 = "v4.0",
}

export enum VariantPickerType {
  AUTO_SELECT = "auto-select",
  MANUAL_SELECT = "manual-select",
}

export enum NonInstantExchangeOrderProvision {
  ALL_PRODUCTS_PROCESSED = "all-products-processed",
  EXCHANGE_PRODUCTS_PROCESSED = "exchange-products-processed",
}

export const doesCustomerPortalSupportBulkReturns: Record<
  CustomerPortalVersion,
  boolean
> = { [CustomerPortalVersion.V3_5]: true, [CustomerPortalVersion.V4_0]: true };

export enum InfoModalVersion {
  V1 = "v1",
  V2 = "v2",
}

export interface DynamicReturnsPricingItemCount {
  type: DynamicReturnTypes.ITEM_COUNT;
  pricePerAdditonalItem: number;
  maxPrice: number;
}

export interface DynamicReturnsPricingCartValue {
  type: DynamicReturnTypes.CART_VALUE;
  maxPrice: number;
  pricingRuleSet: PricingRuleSet;
}

export type DynamicReturnPricing =
  | DynamicReturnsPricingCartValue
  | DynamicReturnsPricingItemCount;
interface ProcessingAutomation {
  exchanges: ReturnStatus;
  refunds: ReturnStatus;
  store_credit: ReturnStatus;
  exchanges_delay: number;
  refunds_delay: number;
  store_credit_delay: number;
}

export interface StripeData {
  customer_id: string;
  card?: Stripe.Card | null;
  paymentMethod?: PaymentMethod;
  secondaryPaymentMethod?: PaymentMethod;
}

export enum RedoItemFulfillment {
  NONE = "none",
  PARTIAL = "partial",
  IMMEDIATE = "immediate",
}

export enum CoverageBillingTrigger {
  ORDER_CREATION = "order_creation",
  REDO_PRODUCT_FULFILLMENT = "redo_product_fulfillment",
}

export type CustomsSettings = {
  defaultContentsType?: ContentsType;
  defaultNonDeliveryOption?: NonDeliveryOption;
  defaultCustomsDeclarationType?: CustomsDeclarationType;
  defaultContentsDescription?: string | null;
  defaultContentsDescriptionSource?: "manual" | "ai_generated";
  customsSigner?: string;
  aesExemptionType?: AESExemptionType | null;
  taxIdUsage?: TaxIdUsage | null;
  uspsExemptionCode?: USPSExemptionCode | null;
  predefinedCustomsDeclarations?: CustomsDeclaration[] | null;
  minHSClassifyConfidenceScore?: NumberRange<0, 1> | null;
};

export const ProcessingAutomationZodSchema = z.object({
  exchanges: ReturnStatusZod,
  refunds: ReturnStatusZod,
  store_credit: ReturnStatusZod,
  exchanges_delay: z.number(),
  refunds_delay: z.number(),
  store_credit_delay: z.number(),
});

export type GiftCardRecipientType = "recipient" | "customer" | "both";
export enum GiftCardRecipientEnum {
  RECIPIENT = "recipient",
  CUSTOMER = "customer",
  BOTH = "both",
}

export const AutomationZodSchema = z.object({
  enable: z.boolean(),
  returnProcessing: ProcessingAutomationZodSchema,
  claimProcessing: ProcessingAutomationZodSchema,
  fulfill_redo_item: createEnum(["none", "partial", "immediate"], {
    enumName: "FulfillRedoItem",
  }),
  returnOrderTags: z.object({ onRefund: z.array(z.string()) }),
  storeCreditExpiration: z.object({ enabled: z.boolean(), days: z.number() }),
  giftCardRecipient: createEnum(GiftCardRecipientEnum, {
    enumName: "GiftCardRecipientEnum",
  }),
  coverageBillingTrigger: createEnum(CoverageBillingTrigger, {
    enumName: "CoverageBillingTrigger",
  }),
});

export interface Automation {
  enable: boolean;
  returnProcessing: ProcessingAutomation;
  claimProcessing: ProcessingAutomation;
  fulfill_redo_item: RedoItemFulfillment;
  returnOrderTags: { onRefund: string[] };
  storeCreditExpiration: { enabled: boolean; days: number };
  giftCardRecipient: GiftCardRecipientEnum;
  coverageBillingTrigger: CoverageBillingTrigger;
}

export enum ExternalReturnHubType {
  RETURNBEAR = "returnbear",
}

// TODO(paul): remove casts
const AddressSchema_ = z.object({
  _id: z.any().optional(),
  externalLocationId: z.string().nullish(),
  name: z.string(),
  street1: z.string(),
  street2: z.string().nullish() as unknown as z.ZodOptional<z.ZodString>,
  city: z.string(),
  zip: z.string(),
  state: z.string().nullish() as unknown as z.ZodOptional<z.ZodString>,
  country: z.string(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  country_name: z.string().nullish(),
  phone: z.string().nullish() as unknown as z.ZodOptional<z.ZodString>,
  email: z.string().nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  trackstarWarehouseId: z.string().nullish(),
  inmarLocationId: z.string().nullish(),
  blueYonderLocationId: z.string().nullish(),
  isSekoHub: z.boolean().nullish(),
  aims360WarehouseId: z.string().nullish(),
  externalReturnHub: z
    .object({
      provider: createEnum(ExternalReturnHubType, {
        enumName: "ExternalReturnHubType",
      }),
      locationId: z.string().nullish(),
    })
    .nullish(),
  returnsCenterId: z.string().nullish(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

export interface Portal {
  _id: string;
  /** @deprecated use brand kit instead */
  font_family: string;
  /** @deprecated use brand kit instead */
  title_font_family: string;
  /** @deprecated use brand kit instead */
  body_font_family: string;
  shippingFeeText: string;
  /** Maybe should be in brand kit but not deprecated right now */
  custom_css: string;
  new_custom_css: string;
  /** @deprecated use brand kit instead */
  font_weight: 100 | 300 | 400 | 500 | 700 | 900;
  /** @deprecated use brand kit instead */
  button_color: string;
  /** @deprecated use brand kit instead */
  accent_color: string;
  /** @deprecated use brand kit instead */
  accent_background_color: string;
  /** @deprecated use brand kit instead */
  home_text_color: string;
  enable_alert: boolean;
  notification: string;
  returnButtonText: string;
  registerButtonText?: string;
  claimButtonText: string;
  notExchangeButtonText?: string;
  notExchangeButtonSubtitle?: string;
  exchangeButtonText?: string;
  standard_exchange_text?: string;
  backorderHeaderText?: string;
  backorderSubtitleText?: string;
  backorderShowDate?: boolean;
  hideDiscountCodes: boolean;
  hideSuggestedProducts: boolean;
  instant_exchange_text: string;
  custom_payment_placeholder_color?: string;
  privacy_link: string;
  /** Maybe should be in brand kit but not deprecated right now */
  background_url: string | null;
  /** @deprecated use brand kit instead */
  favicon_url: string | null;
  /** @deprecated use brand kit instead  */
  logo_url: string | null;
  /** Maybe should be in brand kit but not deprecated right now */
  colors: string[];
  /** Maybe should be in brand kit but not deprecated right now */
  injection_text_color: string;
  header?: string;
  footer?: string;
  green_return_text?: string;
  custom_confirmation_text?: string;
  green_return_confirmation_text?: string;
  green_return_confirmation_description?: string;
  exchangeOptionText?: string;
  storeCreditOptionText?: string;
  refundOptionText?: string;
  claimReviewHeader?: string;
  claimShippingLineItemText?: string;
  domain?: string;
  pathPrefix: string;
  cantFindOrderText?: string;
  claimSummarySubtext?: string;
  warrantySummarySubtext?: string;
  returnSummarySubtext?: string;
  hideOrderStatusUrl?: boolean;
  hideRateYourExperienceWidget?: boolean;
  hideLoginOrderNumberTooltip?: boolean;
  collapseVariantThreshold?: number;
  merchantDisplayName?: string;
  useBetaPackagePickupModal?: boolean;
  socialLoginOptions?: SocialLoginOption[];
}

const teamAnonymizationSchema = z.object({
  anonymizationDate: z.string().nullish(),
  wasAnonymized: z.boolean().nullish(),
});
type TeamAnonymization = z.infer<typeof teamAnonymizationSchema>;

export const IndustryCategoryZodSchema = z.object({
  redoAssignedCategories: z.array(z.string()).nullish(),
  autoAssignedCategories: z.array(z.string()).nullish(),
  allCategoriesIncludingTransitiveCategories: z.array(z.string()).nullish(),
});
export interface IndustryCategory {
  redoAssignedCategories?: string[];
  autoAssignedCategories?: string[];
  allCategoriesIncludingTransitiveCategories?: string[];
}

export enum TeamType {
  Store = "store",
  FulfillmentService = "3PL",
}

export interface Team {
  accountId?: string;
  address?: Address;
  teamType?: TeamType; // TODO @joe/organizations - backfill all existing teams with the store type and then make this required
  ipAddress?: string; // UPS requires this to create ghost accounts
  fulfillmentService?: string;
  storefrontAccessToken?: string | null;
  accessScope: string[];
  billingFailure: "allow" | "restrict";
  billingMethod: MerchantBillingMethod;
  billingAutomation: "auto" | "manual";
  manualBillingDueInDays: number;
  customer_email: string;
  publicAddress?: Address;
  damagedAddress?: Address;
  customInStoreAddresses?: Address[];
  automation: Automation;
  failed_payouts: number;
  shutdown_date: string;
  _id: string;
  email: string;
  adminName: string;
  adminPhone: string;
  industryCategory?: IndustryCategory;
  teamAnonymization?: TeamAnonymization;
  intercomId: string;
  slackChannelId?: string;
  slackChannelIds?: string[];
  lastOrderSync: string;
  orderBackfillWatermark: string;
  orderBackfillMin: string;
  commentsoldBackSyncOrders?: {
    stopAt: string;
    current: string;
    intervalSeconds: number;
  };
  commentsoldOrderSyncAt?: string;
  bigcommerceBackSyncOrders?: { stopAt: string; current: string };
  bigcommerceOrderSyncAt?: string;
  merchantPricePerOrder?: string;
  redoManagedReturnsPricePerOrder?: string;
  name: string;
  notification_email: string;
  notify_on_return_created: boolean;
  notify_on_delivery: boolean;
  notify_on_needs_review: boolean;
  notify_awaiting_processing_report_email_enabled?: boolean;
  notify_awaiting_processing_report_emails?: string[];
  notify_awaiting_processing_report_slack_enabled?: boolean;
  claim_notification_email: string;
  warranty_notification_email: string;
  support_notification_email: string[];
  notify_on_claim_created: boolean;
  notify_on_claim_delivery: boolean;
  notify_on_claim_needs_review: boolean;
  notify_on_warranty_created: boolean;
  notify_on_warranty_needs_review: boolean;
  portal: Portal;
  pricePerOrder: string /** @deprecated use settings.returns.pricePerOrder instead */;
  receipt_email: string;
  returnLocationId?: string;
  settings: Settings;
  redoInternalProducts?: RedoInternalProducts;
  storeUrl: string;
  stripe?: StripeData;
  users: TeamUser[];
  widget_slug: string;
  shopifyCoverageProductSource?: "store" | "supplier";
  storePassword: string;
  _shopify: Record<string, any>;
  createdAt: string;
  updatedAt?: string;

  save(): Promise<void>;
}

export interface DateRange {
  start: string | null;
  end: string | null;
}

export enum DiscountDistributionMethod {
  FAMILY = "DistributeAcrossFamily",
  ORDER = "DistributeAcrossOrder",
  LINE_ITEM = "LineItem",
}

export enum OutOfStockAdvanced {
  CANCEL = "cancel",
  CONTINUE = "continue",
  OVERSELL = "oversell",
}

export enum NonZeroValueExchangeStrategy {
  NONE = "none",
  APPLY_DISCOUNT = "apply_discount",
  MARK_AS_PAID = "mark_as_paid",
  DISCOUNTED_VALUE_MARKED_AS_PAID = "discounted_value_marked_as_paid",
  CREATE_AND_COMPLETE_DRAFT_ORDER = "create_and_complete_draft_order",
  UPSELL_VALUE_MARKED_AS_PAID = "upsell_value_marked_as_paid",
  APPLY_EXCHANGE_DISCOUNT_WITH_TAX_MARK_AS_PAID = "apply_exchange_discount_with_tax_mark_as_paid",
}

export interface PagesData {
  pageId: string;
  pageName: string;
  pageAccessToken: string;
  instagramBusinessAccountId?: string;
  instagramUsername?: string;
}

export enum QuickActionActionType {
  AI_RESPOND = "ai_respond",
  SEND_MESSAGE = "send_message",
  OPEN_LINK = "open_link",
}

const QuickActionAiRespondZod = z.object({
  type: z.literal(QuickActionActionType.AI_RESPOND),
  title: z.string(),
});

const QuickActionSendMessageZod = z.object({
  type: z.literal(QuickActionActionType.SEND_MESSAGE),
  title: z.string(),
  message: z.string(),
});

const QuickActionOpenLinkZod = z.object({
  type: z.literal(QuickActionActionType.OPEN_LINK),
  title: z.string(),
  url: z.string(),
});

export const QuickActionZod = z.discriminatedUnion("type", [
  QuickActionAiRespondZod,
  QuickActionSendMessageZod,
  QuickActionOpenLinkZod,
]);

export type QuickAction = z.infer<typeof QuickActionZod>;

export enum AccountTabSectionType {
  PRODUCT_RECOMMENDATIONS = "product_recommendations",
  VIEWED_PRODUCTS = "viewed_products",
  RECENT_ORDERS = "recent_orders",
  WISH_LIST = "wish_list",
}

export enum CloseActionMethod {
  NEXT = "next",
  TABLE = "table",
  STAY = "stay",
}

export enum SendType {
  SEND = "send",
  CLOSE = "close",
  IN_PROGRESS = "in_progress",
}

export interface AccountTabSection {
  type: AccountTabSectionType;
  header: string;
  enabled: boolean;
}

export const defaultWishListAccountTabHeader = "Wish list";
export const defaultAccountTabSections: AccountTabSection[] = [
  {
    type: AccountTabSectionType.RECENT_ORDERS,
    header: "Recent orders",
    enabled: true,
  },
  {
    type: AccountTabSectionType.WISH_LIST,
    header: defaultWishListAccountTabHeader,
    enabled: true,
  },
  {
    type: AccountTabSectionType.VIEWED_PRODUCTS,
    header: "Jump back in",
    enabled: true,
  },
  {
    type: AccountTabSectionType.PRODUCT_RECOMMENDATIONS,
    header: "Find new favorites",
    enabled: true,
  },
];

export interface EmailInfo {
  name: string;
  email: string;
  integrationKind: string;
}

export interface SupportAiSettings {
  enabled: boolean;
  respondToInternalEmails?: boolean;
  copilotEnabled: boolean;
  copilotModel?: OpenAIModel;
  autoGenerateResponses?: boolean;
  useEmailSignature?: boolean;
  autopilotEnabled?: boolean;
  aiAutopilotTicketVolume?: number;
  automateLiveChat?: boolean;
  enabledPlatforms?: {
    // these are the only platforms supported on autopilot as on 03/14/25
    email?: boolean;
    facebook?: boolean;
    instagram?: boolean;
    sms?: boolean;
    attentive?: boolean;
    postscript?: boolean;
    tiktok?: boolean;
  };
  trialMessagesRemaining?: number;
  hasTriedAi: boolean;
  autoCloseNoReplyTickets?: boolean;
  whitelistedDomains?: string[];
}

export enum ReviewDiscountEvent {
  REVIEW = "review",
  MEDIA = "media",
}

/**
 * The automation strategy defines how a package protection claim (managed claim) is processed.
 *
 * Terms:
 * RO - Redo owned (redo pays merchant for resolution or rejects the claim ourselves)
 * MO - Merchant owned (merchant pays for the claim, same as normal package protection claims)
 *
 * - TIME_BASED_APPROVAL: After a certain amount of time has passed, we will check if the claim has
 *     been approved by the insurance provider. If it has, we will approve the claim as a RO claim.
 *     If it has not, we will reject the claim as a RO claim.
 * - AUTO_REJECT_APPROVE: The claim is automatically rejected as a RO claim after the claim has
 *     been resolved by our insurance provider.
 * - MERCHANT_MR: If the insurance provider rejects the claim, the merchant will go through a manual
 *     review process to approve or reject the claim as a MO claim.
 * - APPROVE: The claim is automatically approved. Should only be used if all claims are covered.
 */
export enum AutomationStrategy {
  TIME_BASED_APPROVAL = "time_based_approval",
  AUTO_REJECT_APPROVE = "auto_reject_approve",
  MERCHANT_MR = "merchant_mr",
  APPROVE = "approve",
}

export enum ShippingClaimAutomationInputType {
  NON_REPORTING = "non_reporting",
  REPORTING = "reporting",
}

export interface AutomationStrategyOptions {
  timeTillApprovalMs?: number;
}

export interface InternationalPriceRoundingSettings {
  enabled: boolean;
  lowerPriceEnding: number;
  upperPriceEnding: number;
  excludedCatalogIds: string[];
}

export interface PackageProtectionSettings {
  enabled: boolean;
  coverage?: boolean;
  customerClaimsEnabled?: boolean;
  packageProtectionPlusEnabled?: boolean;
  packageProtectionPlusPercentage?: number;
  packageProtectionPlusAllClaimsCovered?: boolean;
  packageProtectionPlusAllOrdersCovered?: boolean;
  packageProtectionPlusShippingIncluded?: boolean;
  createSCOrderForManagedClaim?: boolean;
  automationStrategy?: {
    strategy: AutomationStrategy;
    options: AutomationStrategyOptions;
  };
  minPrice: number;
  maxPrice: number;
  percentage: number;
  splitProducts: boolean;
  pricingRuleSet?: PricingRuleSet[];
  minCoverageEligibleCartTotal?: number;
  maxCoverageEligibleCartTotal?: number;
  coverageMlModel?: CoverageMlModelDb;
  fixedSubscriptionPrice?: number;
  newClaimsTables?: boolean;
}

export interface FinalSaleReturnsSettings {
  enabled: boolean;
  /**
   * These are valid collection handles.
   * @deprecated Use validCollectionTitles instead. All of the other collection models use the title,
   * not the handle, so we need to store both. After the migration, we can remove this field.
   */
  validCollections: string[];
  validCollectionTitles: string[];
  validProductTags: string[];
  minPrice: number;
  maxPrice: number;
  percentage: number;
  reimbursementPercentage: number;
  allRedoPurchasesAreFinalSale: boolean;
  allCovered: boolean;
  allEligible?: boolean;
  shippingLabelCoveragePrice?: number;
  pricingRuleSet?: PricingRuleSet[];
  finalSaleRevSharePercentage?: number;
}

interface ReturnItemDispositionSettings {
  enabled: boolean;
  gradeOptions: string[];
  outcomeOptions: string[];
}

export enum ReturnMethod {
  CUSTOMER_SHIPS = "customer_ships",
  CUSTOMER_IN_STORE = "customer_in_store",
  PACKAGE_PICKUP = "package_pickup",
}

export type InternationalReturnPricing = {
  countries: string[];
  multiplier: number;
}[];

export enum ReviewPrioritizationOption {
  HighestPrice = "Highest price",
  LowestReview = "Lowest review",
  HighestReview = "Highest review",
  Random = "Random",
}

export enum LineItemDetailOption {
  VariantInventory = "variantInventory",
  ViewInventoryDetails = "viewInventoryDetails",
}

export interface MetafieldDisplayConfig {
  /** The full metafield key in "namespace.key" format */
  key: string;
  displayName?: string;
}

export enum AgentReviewAction {
  APPROVE = "approve",
  DENY = "deny",
  FLAG = "flag",
  NONE = "none",
}

export enum AgentReviewThreshold {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export enum AgentReviewRiskLevel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

/**
 * Configuration for what action to take for a specific risk level.
 * Each risk level (low, medium, high) can have its own action.
 */
export interface AgentReviewRiskConfig {
  /** The risk level this config applies to */
  riskLevel: AgentReviewRiskLevel;
  /** The action to take when this risk level is identified */
  action: AgentReviewAction;
}

export type InstantExchangeHoldKind = "none" | "one_dollar" | "full_cost";

type ContactFormFieldSettings = {
  /** Whether the field is shown in the contact form */
  show: boolean;
  /** Whether the customer is required to fill out the field in order to submit the form */
  required: boolean;
  /** Placeholder text shown before the customer provides a value for the field */
  placeholder: string;
};

export enum ContactFormFieldType {
  Text = "text",
  Dropdown = "dropdown",
}

export type ContactFormCustomFieldSettings = {
  id: string;
  label: string;
  required: boolean;
  placeholder: string;
  type: ContactFormFieldType;
  options?: string[];
};

export type ContactFormSettings = {
  enabled: boolean;
  title: string;

  name: ContactFormFieldSettings;
  email: Omit<ContactFormFieldSettings, "show" | "required">;
  subject: ContactFormFieldSettings & { options: string[] };
  phone: ContactFormFieldSettings;
  comment: Omit<ContactFormFieldSettings, "show" | "required">;

  allowFileUpload: boolean;
  customFields: ContactFormCustomFieldSettings[];

  replyEmail: EmailInfo | undefined;
  successMessage: string;
  customCss: string;
  font: string;
  collapsibleForm: boolean;
  showActions: {
    quickActions: boolean;
    orderEditing: boolean;
    returnPortal: boolean;
    faq: boolean;
  };
  submitButton: { label: string; color: string };
};

export const contactFormSettingsDefault: ContactFormSettings = {
  enabled: false,
  title: "Contact Us",
  name: { show: true, required: false, placeholder: "Enter your name" },
  email: { placeholder: "Enter your email" },
  subject: {
    show: true,
    required: false,
    placeholder: "Select a subject",
    options: ["General Inquiry", "Order Inquiry", "Feedback", "Other"],
  },
  phone: {
    show: true,
    required: false,
    placeholder: "Enter your phone number",
  },
  comment: {
    placeholder:
      "Leave a comment to help us better know what you're contacting us about.",
  },
  allowFileUpload: false,
  customFields: [],
  replyEmail: undefined,
  successMessage:
    "Thanks for contacting us. We'll get back to you as soon as possible.",
  customCss: "",
  font: "inherit",
  collapsibleForm: false,
  showActions: {
    quickActions: false,
    orderEditing: false,
    returnPortal: false,
    faq: false,
  },
  submitButton: { label: "Submit", color: "inherit" },
};

export interface SupportVoiceBilling {
  pricePerMinuteInCents?: number | null;
  subscriptionActive: boolean;
  pricePerMonthInCents?: number | null;
}
export enum SupportBaseBillingVersion {
  FLAT_RATE = "flat_rate",
  BUCKETED_USAGE = "bucketed_usage",
  /** Simple per-ticket pricing for human-handled tickets */
  SIMPLE_HUMAN_USAGE = "simple_human_usage",
  /** Per-order billing - charges per order using the same logic as checkout optimization */
  PER_ORDER = "per_order",
}

export interface BaseSupportBilling {
  pricePerMonthInCents?: number | null;
  subscriptionActive: boolean;
  billingStartDate?: Date | null;
  canceledBillingAt?: Date | null;
  freeTrialEndDate?: Date | null;
  version?: SupportBaseBillingVersion;
  basePercentageDiscount?: number | null;
  basePercentageDiscountEndDate?: Date | null;
  baseDiscountInCents?: number | null;
  baseDiscountEndDate?: Date | null;
  /** Price per human ticket in cents - used for SIMPLE_HUMAN_USAGE billing */
  humanTicketPriceInCents?: number | null;
  /** Price per order in cents - used for PER_ORDER billing */
  pricePerOrderInCents?: number | null;
}

export interface MarketingSmsBilling {
  enabled: boolean;
  startDate?: string;
  period: BillingPeriod;
  periodFeeInCents: number;
  freeSmsCreditsPerMonth: number;
  overageSmsPriceInThousandthCents: UsdThousandthCents;
  overageMmsPriceInThousandthCents: UsdThousandthCents;
  renewOnPeriodEnd: boolean;
  targetBillingDay?: number | null;
  paymentProvider?: PaymentProviderType;
  ignoreCarrierFees: boolean;
}

export interface ProductUpdateSyncFields {
  tags?: boolean | null;
  title?: boolean | null;
  sku?: boolean | null;
}

export interface MarketingEmailBilling {
  enabled: boolean;
  startDate?: string;
  period: BillingPeriod;
  periodFeeInCents: number;
  freeEmailsPerMonth: number;
  pricePerThousandInCents: number;
  renewOnPeriodEnd: boolean;
  targetBillingDay?: number | null;
  paymentProvider?: PaymentProviderType;
}

interface MarketingSupportConnectionSettings {
  preferredEmail?: string;
}

const merchantInsightFieldSchema = z.object({
  insight: z.string(), // e.g., "Shops mostly late at night on mobile; responds well to flash sales"
  confidence: z.number().min(1).max(100), // Confidence in the insight
});

export const merchantInsightsSchema = z.object({
  scale: merchantInsightFieldSchema.optional(),
  position: merchantInsightFieldSchema.optional(),
  strategy: merchantInsightFieldSchema.optional(),
  engagement: merchantInsightFieldSchema.optional(),
});

export const recommendationSettingsSchema = z.object({
  strategy: z.string(),
  version: z.number(),
  updatedAt: z.string(),
  primaryVendors: z.array(z.string()).nullish(),
});

export type RecommendationSettings = z.infer<
  typeof recommendationSettingsSchema
>;

export interface MarketingAiSettings {
  enabled: boolean;
  bio?: string;
  assumptionTemperature?: number;
  insights?: z.infer<typeof merchantInsightsSchema>;
}

interface LoyaltySettings {
  enabled: boolean;
  // TODO: add storefrontPopup and storefrontEmbedding (see MarketingSettings)
}

export interface MarketingSettings {
  enabled: boolean;
  smsBilling?: MarketingSmsBilling;
  emailBilling?: MarketingEmailBilling;
  textsEnabled: boolean;
  bypassPhoneNumber: boolean;
  subscriptionEntryPoints: { login: boolean; saveForLater: boolean };
  customerImportsEnabled?: boolean;
  campaigns?: { enabled?: boolean };
  smartSending?: { secondsBetweenSends: number; enabled: boolean };
  smsStoreName?: string;
  linkShortenerSubdomain?: string;
  emailConversions?: string[];
  supportConnection?: MarketingSupportConnectionSettings;
  ai?: MarketingAiSettings;
  loyalty?: LoyaltySettings;
  texasSms?: { excludeSegment?: boolean; texasSegmentId?: string };
  /** Temporary measure to control rollout of commentsold marketing integration */
  showCommentsoldIntegration?: boolean;
  marketingAgent?: { enabled: boolean };
  backInStock?: { buttonSelector?: string };
}

export interface EmailFooterSettings {
  useCustomAddress?: boolean;
  customAddress?: Address;
}

export interface RecoverSettings {
  enabled: boolean;
  defaultRepName?: string;
  suffixOverride?: string;
  merchantNotes?: string;
  externalSmsMarketingProvider?: ExternalSmsMarketingProvider;
  billing?: RecoverBillingSettings;
  attribution?: RecoverAttribution;
}

export interface securitySettings {
  methods?: { deviceAuthAndPin?: boolean };
}

export interface SupportSettings {
  enabled: boolean;
  billing?: {
    textMessaging?: TextMessagingBilling;
    ai?: SupportAiBillingZod;
    base?: BaseSupportBilling;
    voice?: SupportVoiceBilling;
    paymentProvider?: PaymentProviderType;
  };
  csat?: {
    enabled: boolean;
    surveySendTime?: string;
    surveyMethods: string[];
    allowRemarks?: boolean;
    headerImage?: string;
    surveyMessage?: string;
  };
  closeAction: CloseActionMethod;
  defaultSendAction?: SendType;
  useInProgressStatus?: boolean;
  keepAssigneeOnTicketReopen?: boolean;
  closeTicketsOnGmailArchive?: boolean;
  showCreatedDateColumn?: boolean;
  /** Controls the contact form merchants can embed on their website */
  contactForm?: ContactFormSettings;
  receiveEmail?: string;
  senderEmails?: SenderEmail[];
  emailSignature?: string;
  aiEmailSignature?: string;
  tags?: ConversationTag[];
  // TODO: remove
  bccEmails?: boolean;
  tagSpam?: boolean;
  ticketAssignment: "manual" | "balanced" | "round-robin";
  usersForAutoAssignment: string[];
  autoAssignOnResponse?: boolean;
  autoReassignOnResponse?: boolean;
  autoReassignFromAiOnResponse?: boolean;
  autoReassignFromHumanOnResponse?: boolean;
  maxTicketsPerUser: string;
  metaPagesData?: {
    userId?: string;
    longLivedToken?: string;
    pagesData?: PagesData[];
  };
  excludedEmails?: string[];
  ai?: SupportAiSettings;
  voice?: TeamVoiceConfiguration;
  sms?: TeamSmsConfiguration;
  instagram?: {
    autoCloseMentions: boolean;
    autoCloseRepliedStories: boolean;
    commentsEnabled: boolean;
    autoCloseExternal?: boolean;
  };
  facebook?: { autoCloseExternal?: boolean; commentsEnabled: boolean };
  propagateDeletes?: boolean;
  faq?: IFaqTheme;
  autoMergeEnabled?: boolean;
  defaultEmail?: string;
  undoSend?: boolean;
}

export interface IndustryCategory {
  redoAssignedCategories?: string[];
  autoAssignedCategories?: string[];
  allCategoriesIncludingTransitiveCategories?: string[];
}

interface CheckoutButtonsSettings {
  addProductOnOptIn: boolean;
}

export enum ExchangeType {
  VARIANT = "variant",
  ADVANCED = "advanced",
}

export interface OrderTrackingBillingSettings {
  enabled: boolean;
  invoiceBilling?: OrderTrackingInvoiceBillingSettings | null | undefined;
  continueIfNotAccepted: boolean;
  rerouteBillingToMarketing?: boolean;
  pricePerOrder: number;
  freeOrdersPerMonth: number;
  freeUntilDateEnabled: boolean | null;
  freeUntilDate: string | null;
  texts: { pricePerSms: number; pricePerMms: number };
  unbilledUsage?: {
    orderCount?: number | null;
    smsCount?: number | null;
    mmsCount?: number | null;
  } | null;
}

export interface OrderEditingTools {
  cancelOrder: {
    enabled: boolean;
    cancelOrderTimeWindowAfterPlacingMinutes: number;
  };
  changeAddress: {
    enabled: boolean;
    changeOrderAddressTimeWindowAfterPlacingMinutes: number;
    requireConfirmation: boolean;
  };
  addOrderDiscount: {
    enabled: boolean;
    addOrderDiscountTimeWindowAfterPlacingMinutes: number;
  };
  addOrderGiftNote: {
    enabled: boolean;
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: number;
  };
  updateOrder: {
    enabled: boolean;
    updateOrderTimeWindowAfterPlacingMinutes: number;
    addItem: boolean;
    removeItem: boolean;
    changeVariant: boolean;
  };
}

export interface OrderEditingEnabledPlatforms {
  shopperAI: boolean;
  supportSelfServe: boolean;
  customerPortal: boolean;
  orderTracking: boolean;
}

export interface OrderEditingHolds {
  applyHoldToAllOrders: boolean;
  holdDurationInMinutes: number;
  holdOrdersRequiringPayment: boolean;
  holdStatusStrategy?: HoldStatusStrategy;
}

export interface OrderEditingSettings {
  holds?: OrderEditingHolds | null;
  tools?: OrderEditingTools | null;
  useOmsEvents?: boolean;
  enabledPlatforms?: OrderEditingEnabledPlatforms;
}

export enum CarrierMappingMatchType {
  EXACT = "exact",
  STARTS_WITH = "starts-with",
  ENDS_WITH = "ends-with",
  CONTAINS = "contains",
  CATCH_ALL = "catch-all",
  REGEX = "regex",
}

export enum CustomAttributeMatchType {
  EQUALS = "equals",
  NOT_EQUALS = "notEquals",
}

export interface CarrierMapping {
  value: string;
  matchType: CarrierMappingMatchType;
  targetCarrier: string;
  caseSensitive: boolean;
}

export interface IOrderTrackingSettings {
  enabled: boolean;
  createTrackers: boolean;
  stalledDays?: number; // stalled in transit
  stalledDaysFulfillment?: number; // stalled in fulfillment
  excludeTrackMyOrderClicks: boolean;
  smsEnabled: boolean;
  skipSmsOptInRequirement?: boolean;
  combineCrossOrderShipments?: boolean;
  useRedoTrackingUrl: boolean;
  ads?: OrderTrackingAd[];
  upsellProducts?: UpsellProductSection;
  lineItemsSection?: LineItemsSectionSettings;
  internalNotifications?: {
    // TODO (Josh) replace with new advanced flow enums
    [TriggerType.ORDER_STALLED]?: boolean;
    [TriggerType.ORDER_DELAYED]?: boolean;
    [TriggerType.ORDER_ARRIVING_EARLY]?: boolean;
    [TriggerType.ORDER_RETURN_TO_SENDER]?: boolean;
    [TriggerType.ORDER_DELIVERY_ATTEMPTED]?: boolean;
    [TriggerType.ORDER_FAILURE]?: boolean;
    [TriggerType.ORDER_ERROR]?: boolean;
    [TriggerType.ORDER_CANCELLED]?: boolean;

    // [TriggerType.RETURN_STALLED]?: boolean;
    // [TriggerType.RETURN_DELAYED]?: boolean;
    // [TriggerType.RETURN_RETURN_TO_SENDER]?: boolean;
    // [TriggerType.RETURN_DELIVERY_ATTEMPTED]?: boolean;
    // [TriggerType.RETURN_FAILURE]?: boolean;
    // [TriggerType.RETURN_ERROR]?: boolean;
    // [TriggerType.RETURN_CANCELLED]?: boolean;
    email?: string;
  };
  billing?: OrderTrackingBillingSettings | null;
  mostRecentSync?: {
    fulfillmentsAttempted: number;
    fulfillmentsFailed: number;
    syncDate: string;
    ordersFullySynced: number;
    ordersNotFullySynced: number;
  };
  /** @deprecated */
  syncInProgress?: boolean;
  syncInProgressStartedAt?: Date;
  consecutiveSyncFailures?: number;
  postPurchaseUpsell?: {
    enabled: boolean;
    billing?: OneClickUpsellBilling;
    type: UpsellProductSource;
    collection?: CollectionInfo;
    discountEnabled: boolean;
    discountType: DiscountValueType;
    discountAmount: string;
    createOrderOnUpsell?: boolean;
  };
  /** Overrides how long until the tracking link expires */
  authExpirationDays?: number;
  /** When enabled, preserves the original carrier in EasyPost tracker sync requests */
  alwaysPreserveCarrierInTrackerSyncs?: boolean | null;
  /** Hours to continue retrying tracker syncs regardless of recovery attempts count */
  keepRetryingTrackerSyncsForAtLeastHours?: number | null;
  /** Custom carrier code mappings for tracker syncing */
  carrierMappings?: CarrierMapping[];

  payloadProvider?: OrderTrackingOrderPayloadProvider;
  orderMaxLifeCycleDays?: number;
  shipmentMaxLifeCycleDays?: number;
  trackingPageUrl?: string;
}

export const DEFAULT_PER_RETURN_BILLING_FEE = 1.5;
export interface PerReturnBillingSettings {
  enabled: boolean;
  usageFee: number;
}

export const DEFAULT_RETURNS_PROCESSING_FEE = 1.0;
export const DEFAULT_RETURNS_PROCESSING_COST = 0.0;

export enum ReturnProcessingLevel {
  VERIFICATION = "verification",
  GRADING = "grading",
  REFURBISHING = "refurbishing",
  REPACKING = "repacking",
}

export interface redoManagedReturnsSettings {
  enabled: boolean;
  processingFee: number;
  processingCost: number;
  defaultReturnsCenterId?: string;
  returnProcessingLevel?: ReturnProcessingLevel;
}

export const DEFAULT_EXCHANGE_AGENT_USAGE_FEE = 3.5;
export const DEFAULT_EXCHANGE_AGENT_USAGE_FEE_PERCENT = 0;

export const exchangeAgentBillingFeeType = ["flat", "percentage"] as const;
export type ExchangeAgentBillingFeeType =
  (typeof exchangeAgentBillingFeeType)[number];

export const exchangeAgentBillingType = ["shopify", "payout_order"] as const;
export type ExchangeAgentBillingType =
  (typeof exchangeAgentBillingType)[number];

export const closeShopifyReturnOptions = [
  "enabled",
  "greenReturnsOnly",
  "disabled",
] as const;
export type CloseShopifyReturnOption =
  (typeof closeShopifyReturnOptions)[number];

/** Default price per human ticket in cents for SIMPLE_HUMAN_USAGE billing */
export const DEFAULT_HUMAN_TICKET_PRICE_IN_CENTS = 20;

export interface BaseSubscriptionBillingSettings {
  enabled: boolean;
  startDate?: Date | null;
  period: BillingPeriod;
  periodFeeInCents: number;
  renewOnPeriodEnd: boolean;
  targetBillingDay?: number | null;
  paymentProvider?: PaymentProviderType;
}

/**
 * Redo Admin only settings for Exchange Agent billing
 */
export interface ExchangeAgentBillingSettings
  extends BaseSubscriptionBillingSettings {
  /** Is AI Exchange allowed at all? */
  enabled: boolean;
  /** Are we trying to bill for AI Exchange? */
  enableBilling: boolean;
  /** Flat fee per exchange (used when billingFeeType is "flat") */
  usageFee: number;
  /** Percentage of exchange value to bill (used when billingFeeType is "percentage") */
  usageFeePercent: number;
  /** Whether to bill flat fee per exchange or percentage of exchange value */
  billingFeeType: ExchangeAgentBillingFeeType;
  /** Billing method: "shopify" for Shopify App Usage billing, "payout_order" for per-exchange payout orders */
  billingType: ExchangeAgentBillingType;
}

export interface TagExclusionRule {
  sourceTag: string;
  excludedTags: string[] | null;
}

/**
 * Merchant facing settings for Exchange Agent features.
 */
export interface ExchangeAgentMerchantSettings {
  enabled: boolean; // Does the merchant want to use Exchange Agent?
  displayExchangeValueAsDiscount: boolean;
  customerVariantPreferencesEnabled: boolean;
  selectInitialVariant: boolean;
  variantRecommendations: {
    enabled: boolean;
    productsAsVariantsEnabled: boolean;
  };
  productRecommendations: {
    enabled: boolean;
    limit: number;
    merchantRecommendedExchangesEnabled: boolean;
    tagExclusionRules?: TagExclusionRule[] | null;
  };
  allowNewFlowAbTest?: boolean | null;
  forceNewFlow?: boolean | null;
}

export type ImsStatus = "read-only" | "pending" | "source-of-truth";

export interface ImsForecastingBillingSettings
  extends BaseSubscriptionBillingSettings {
  useSubscriptionPricing?: boolean;
  customPriceInCents?: number;
}

export interface ImsSettings {
  enabled: boolean;
  syncEnabled: boolean;
  status: ImsStatus;
  sourceOfTruthSince?: Date | null;
  forecastingEnabled?: boolean;
  pricingRulesEnabled?: boolean;
  lastForecastAt?: Date | null;
  catalogEnabled?: boolean;
  catalogSourceOfTruthStatus?: ImsStatus;
  catalogSourceOfTruthSince?: Date | null;
  productSyncVersion?: number;
  subscriptionBilling?: BaseSubscriptionBillingSettings;
  forecastingBilling?: ImsForecastingBillingSettings;
  selfEnroll?: boolean;
}

export interface AeoSettings {
  enabled: boolean;
  subscriptionBilling?: BaseSubscriptionBillingSettings;
}

export interface AcpSettings {
  enabled: boolean;
  bearerToken: string;
  hmacSecret: string;
}

export interface RecommerceSettings {
  marketplace: boolean;
  consignmentPercentage?: number;
}

export interface ReFulfillmentSettings {
  enabled: boolean;
  revSharePercentage?: number;
}

export interface Recycling {
  enabled?: boolean;
  recyclableProductsCollection?: { id: string; name: string };

  productId?: string;
  labelPayer: Payer;
  chargeMerchantPerRecycleRequest: boolean;
  perRecycleRequestFee: number;
  minRecyclingProductQuantity: number;
}

export interface Settings {
  recycling?: Recycling;
  concierge: ConciergeSettings;
  cartOptimization?: CartOptimizationSettings;
  checkoutOptimization?: CheckoutOptimizationSettings;
  landedCost?: LandedCostSettings;
  predictiveConversion?: PredictiveConversionSettings;
  crossSellPopoutEnabled: boolean;
  classifyProductsEnabled?: boolean;
  tnpl?: TnplSettings;
  ims?: ImsSettings;
  aeo?: AeoSettings;
  acp?: AcpSettings;
  commentsold: {
    sendDropShipperEmail?: boolean;
    useShopifyStorefront: boolean;
    recProductExclusions?: {
      variantIds?: string[];
      parentIds?: string[];
      tags?: string[];
    };
  };
  products: {
    barcode?: {
      size: {
        height: { value: Decimal128Extended; unit: string };
        width: { value: Decimal128Extended; unit: string };
      };
      topDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS;
      bottomDisplayOption: BARCODE_HEADER_FOOTER_OPTIONS;
    };
    hazmat: { lithiumBattery: { tagsInclude: string[] } };
  };
  discountDistributionMethod: DiscountDistributionMethod;
  /** This is an array for legacy reasons, but it should only ever have one element. */
  shopifySubscription?: ShopifySubscription[];
  shopifySubscriptionMetadata: {
    isAtShopifyBillingCap: boolean;
    pendingCapRaiseUrl?: string;
  };
  inventory: {
    ignoreInventory: boolean;
    ignoreInventoryForReturnTypes: ReturnTypeEnum[];
    followShopifyInventoryPolicy: boolean;
    minimum_stock: number;
    restock: boolean;
    restockDamaged: boolean;
    restockShopifyRefunds?: boolean;
    allowRestockBeforeProcess: boolean;
    disposeShopifyReverseFulfillmentOnRestock: boolean;
  };
  labelExpiration: {
    days: number;
    reminderEmails: boolean;
    expirationEmails: boolean;
    reminderEmailDays: number[];
    strategy: LabelExpirationStrategy;
    compensationMethodsToRemind: CompensationMethod[];
    shouldReopenReturnWhenLabelUsed: boolean;
    reopenReturnAfterDays: number;
  };
  chatFlow: Json;
  useShadowRootInCart?: boolean;
  cartShadowRootSelector?: string;
  checkboxFontSize?: number;
  checkboxSelector?: string;
  checkboxText?: string;
  claimFlow: Json;
  /** Hides products in the return portal so they can't be returned */
  portalExcludedProductTags?: string[];
  portalExcludedCollections?: string[];
  portalExcludedProductIds?: string[];
  checkboxTextAfterRedo?: string;
  countries: string[];
  countriesPackageProtection: string[];
  coverage: Coverage;
  subscriptionEnabled: boolean; //Coverage and Subscription are exclusive. Either coverage is enabled or subscription is enabled but not both.
  /**
   * This boolean represents whether we should sell returns coverage to items that are eligible for returns.
   */
  coverageEnabled: boolean;
  coverageExcludeCollections: string[];
  coverageExcludeCustomerTags: string[];
  coverageExcludeSources: string[];
  coverageExcludeTags: string[];
  coverageExcludeProductTags: string[];
  coverageExcludeDiscountCodes: string[];
  coverageExcludeProperties: string[];
  coverageExcludeCentEnding: string[];
  additionalCoverageWebSources?: string[];
  createReturnBarcodes?: boolean;
  bulkReturnsEnabled?: boolean;

  labelIncludeReturnType?: boolean;
  loadAllAdvancedExchangeProducts?: boolean;
  packagePickupVariants?: PackagePickupVariant[];
  excludeCoverageIfAnyInvalidProduct?: boolean;
  discountCodePartialMatch?: boolean;
  createdAt: string;
  customerSupport: {
    wrongProduct: CustomerSupportSetting;
    damagedItem: CustomerSupportSetting;
  };
  enabled: boolean;
  exchanges: {
    advancedExchanges: AdvancedExchanges;
    selection: ExchangesSelection;
    allowInstant: boolean;
    instantHoldAmount: InstantExchangeHoldKind;
    instantExpirationDays: number;
    notes: {
      includeDate: boolean;
      includeExistingNote: boolean;
      addNoteToOriginalOrders: boolean;
    };
    shopSite: { navigationMode: ShopSiteNavigationMode };
    suffix: string | null;
    shouldStripHash: boolean;
    orderName: {
      prefix: string;
      suffix: string;
      orderNamesByReturnType: {
        prefix: string;
        suffix: string;
        returnType: ExchangeOrderPrefixTypes;
      }[];
      useShopifyGeneratedName: boolean;
      duplicateOrderNumberFormat?: string;
    };
    outOfStockPolicy: RefundType;
    excessExchangeValue: RefundType;
    outOfStockAdvanced: OutOfStockAdvanced;
    excludeProductTags: string[];
    recreateExpiredDiscounts: boolean;
    recreateAutomaticDiscountsAsCodes: boolean;
    blockDiscountsOnExchanges: boolean;
    shopSiteURL: string;
    shopSiteParams: string;
    shopSiteDomain: string;
    shopSitePath: string;
    enableOnHoldOrders: boolean;
    onHoldOrdersOnUpsellOnly?: boolean;
    differentPricedVariantExchanges: boolean;
    excludeDifferentPriceVariants?: boolean;
    variantExchangeProductTags: string[];
    usePreDiscountPrice: boolean;
    instantExchangeOnly?: boolean;
    shopSiteUseCartDiscounts?: boolean;
    reopenExpiredInstantUponShipment?: boolean;
    instantExchangeAutoRefundIfExpired: boolean;
    createLabelOnDraftOrderComplete: boolean;
    exchangeOptionsPreviewShowOriginalPrice?: boolean;
    nonZeroValueExchange?: boolean;
    nonZeroValueExchangeStrategy?: NonZeroValueExchangeStrategy;
    excludedItemProperties?: { name: string; value: string }[];
    defaultPosRestock?: boolean;
    applyOriginalOrderDiscounts: ExchangeType[];
    applyAutomaticDiscounts: ExchangeType[];
    allowCustomerDiscountCodes: boolean;
    hideOutOfStockVariants?: boolean;
    exchangeShippingRateTables?: ShippingRateTable[];
    nonUpsellExchangeOrderPaymentGateway?: {
      enabled?: boolean;
      gatewayName?: string;
    };
    addAdditionalExchangeTags?: boolean;
    shouldRemoveHoldTagWhenRelease?: boolean;
    variantPickerType: VariantPickerType;
    exchangeBonusUpsellPageEnabled?: boolean;
    nonInstantExchangeOrderProvision: NonInstantExchangeOrderProvision;
    separateQuantityForExchangeProducts?: boolean;
    reapplyDiscountAmount?: boolean;
  };
  failed_payouts: number;
  hideCheckboxBranding: boolean;
  hideCheckboxPrice: boolean;
  hidePortalBranding: boolean;
  hidePoweredByInPortal?: boolean;
  hideCheckbox: boolean;
  images: string[];
  locations: ShippingLocation[];
  returnInStoreEnabled?: boolean;
  returnInStoreRadiusMiles?: number;
  inStoreDropOffPackingSlipEnabled?: boolean;
  merchantCoverage: Coverage;
  returnAdjustment: { positiveName: string; negativeName: string };
  paidModel: PaymentModel;
  policyType: string;
  productName?: string;
  protectionExcludeTags?: string[];
  receipt_email: string;
  redoAutoCheck: boolean;
  packageProtectionAutoCheck: boolean;
  returnFlow: Json;
  finalizeReturnFlow: Json;
  finalizeClaimFlow: Json;
  warrantyFlow: Json;
  returnItemDisposition: ReturnItemDispositionSettings;
  returnsUI?: {
    defaultCollapsed?: boolean;
    aiReviewDefaultCollapsed?: boolean;
    lineItemDetailOptions?: LineItemDetailOption[];
    productMetafieldDisplays?: MetafieldDisplayConfig[];
    variantMetafieldDisplays?: MetafieldDisplayConfig[];
    exposeExternalId?: boolean;
    useNewProcessingExperience?: boolean;
  };
  returnLocationId: string;
  returnPortal: string;
  returnPortalHeaderText?: string;
  returnPortalMissingOrderHeaderText?: string;
  returnPortalButtonText?: string;
  returnPortalGiftButtonText?: string;
  emailLoginLabelText?: string;
  orderNumberLoginLabelText?: string;
  loginSubtitleText?: string;
  registrationPortalTitle?: string;
  shippingCarrierOverride: string[];
  shippingCarrierAccounts?: string[];
  shippingInsurance: ShippingInsurance;
  shippingServiceOverride: Map<string, string[]>;
  generateQrCodes: boolean;
  /** When true, merchants see all carrier rates in the create/edit label modal, ignoring their carrier override settings */
  ignoreTeamCarrierOverrideInMerchantDash?: boolean;
  shopAgainAction:
    | "gift_card"
    | "discount_code"
    | "rise"
    | "shopify_account"
    | "creditsyard";
  riseTrigger: { url: string } | null;
  sku?: string;
  sku2?: string;
  returnsSKU?: string;
  packageProtectionSKU?: string;
  combinedSKU?: string;
  uniqueVariantSKU: boolean;
  indexBasedUniqueVariantStartingAt?: number;
  html?: string;
  updatedAt: string;
  upsert: { enabled: boolean; value: string; type: string };
  widget: { url: string };
  packingSlipEnabled?: boolean;
  manualLabelPackingSlipEnabled?: boolean;
  customerLabelUpchargeFee?: number;
  dontChargeCustomerLabelUpchargeWhenOnlyOption?: boolean;
  excludeCustomerLabelFeeForExchanges?: boolean;
  deductLabelFromCredit?: boolean;
  deductLabelFromRefund?: boolean;
  cartToggleEnabled: boolean;
  cartToggleSelector?: string;
  cartClickListenerSelector?: string;
  security?: securitySettings;
  toggleOnColor: string;
  toggleOffColor: string;
  toggleCircleColor: string;
  cartTogglePlacement?: InsertionPosition;
  cartReplaceItems?: boolean;
  packingInstructions?: { [key in ReturnMethod]: string[] };
  exchangeGroups: ExchangeGroup[];
  shopifyExtension?: {
    exchangeBannerPositionBottom?: boolean;
    exchangeBannerInsertBeforeBody?: boolean;
    hideRedoProduct?: HideRedoConfig;
    customizationVisible?: boolean;
    customizeCartItem?: boolean;
    brandPrefix?: string;
    logo?: string;
    redoProductImage?: string;
    imageUpdated?: boolean;
    displayIcon?: boolean;
    iconColor?: string;
    iconBackgroundColor?: string;
    icon?: Icons;
    returnNameOverride?: string;
    packageProtectionNameOverride?: string;
    bothProductNameOverride?: string;
    checkoutExtensionEnabled?: boolean;
    checkoutAnalyticsInCheckoutToggleWidgetEnabled?: boolean;
    attachmentStrategy?: AttachmentStrategy;
    toggleSubtextEnabled?: boolean;
    toggleTextOptions?: {
      returnToggle: ToggleFields;
      packageProtectionToggle: ToggleFields;
      bothProductToggle: ToggleFields;
    };
    infoIconLink?: string;
    customPdpCss?: string;
    customToggleCss?: string;
    forceIPAddressLocation?: boolean;
    coverageExcludePickup?: boolean;
    usingCartAndCheckoutToggle?: boolean;
    forceDoubleCheckoutToggle?: boolean;
    setCheckoutToggleBasedOnOptInCartAttribute?: boolean;
    hideCheckoutUiWhenOptedIn?: boolean;
    openShadowRoot?: boolean;
    checkoutUICustomizations?: CheckoutUICustomizationSettings;
    groupNearbyToggleElementsTogether?: boolean;
    termsAndConditions?: TermsAndConditions;
    clickStandardCheckoutButton?: ClickStandardCheckoutButton;
    widgetConfigs?: WidgetConfig[];
    checkoutButtonsSettings?: CheckoutButtonsSettings;
    redoVariantBaseName?: string;
    useBuyNowCoverage?: boolean;
    customBuyNowCoverageCss?: string;
    buyNow?: { customCss?: string };
    autoNavigateToCheckoutAfterTimeout?: boolean;
    shouldNotUseStorefrontApi?: boolean;
    disableBusinessAccountInStorefront?: boolean;
    preventPageRefreshAfterCartAdd?: boolean;
    dispatchResizeAfterItemsPlaced?: boolean;
    trackRedoProductInventory?: boolean;
    coverageCreditIncentive?: CoverageCreditIncentive;
  };
  returns?: {
    enabled: boolean;
    refundAllReturnedItems: boolean;
    skipExchangeRefunds: boolean;
    pricePerOrder?: string;
    dynamicPricing?: DynamicReturnPricing;
    settlementEnabled: boolean;
    settlementRefund?: number;
    multipleLabelsEnabled?: boolean;
    internationalPricing?: InternationalReturnPricing;
    internationalPriceRounding?: InternationalPriceRoundingSettings;
    finalSaleReturns?: FinalSaleReturnsSettings;
    allowMultiOrderReturns?: boolean;
    multiOrderReturnLimit?: number;
    disableRejectedItems?: boolean;
    merchantDisableRejectedItems?: boolean;
    allowReopenAfterProcessing?: boolean;
    hideGiftCardBranding?: boolean;
    oneShipmentPerItemEnabled?: boolean;
    conditionalSeparateProductTypes?: string[];
    /** For green international returns. shopper will be told to buy their own label.*/
    buyYourOwnLabel?: boolean;
    byolDomestically?: boolean;
    returnToFulfillmentOrigin?: boolean;
    checkForTryNowTags?: boolean;
    shouldAutoProcessRedoReturnWhenShopifyReturnCloses?: boolean;
    enableCustomerPortal?: boolean;
    enableShopAppReturns?: boolean;
    /** Controls whether warehouse location is displayed on return details page */
    warehouseLocationEnabled?: boolean;
    useNewSubmitReturn?: boolean;
    dontChargeMerchantForDamagedOrWrongProductReturnLabels: boolean;
    removeMerchantPaidLabelUpcharge?: boolean;
    agentChatEnabled?: boolean;
    agentReviewEnabled?: boolean;
    /**
     * @deprecated Use agentReviewRiskConfigs instead
     */
    agentReviewAvailableActions?: {
      action: AgentReviewAction;
      threshold: AgentReviewThreshold;
    }[];
    /** Configuration for what action to take for each risk level */
    agentReviewRiskConfigs?: AgentReviewRiskConfig[];
    manualLabelInstructions?: string[];
    merchantCarrierReturnLabels: MerchantCarrierReturnLabelSettings;
    enableMerchantReturnFlow?: boolean;
    allowShipmentSelectionForMerchantCreated?: boolean;
    merchantCreatedClaimsRequireShipping?: boolean;
    waiveRefundStoreCreditBelowAmount?: number;
    enableGiftReceipts?: boolean;
    addReturnTagToOriginalOrder?: boolean;
    missingBuyerInfoDescription?: string;
    permissions?: {
      allowedCompensationMethods?: string[];
      allowEditReturn?: boolean;
      allowEditFeesAndBonuses?: boolean;
      allowEditGreenReturns?: boolean;
    };
    useFixedPackageDimensions?: boolean;
    fixedPackageDimensions?: {
      length?: number;
      width?: number;
      height?: number;
      weight?: number;
    };
    defaultReimburseOriginalShipping?: boolean;
    // Bulk returns configuration
    bulkReturns?: {
      // Collections that define which products can be bulk returned
      collections?: string[];
      // Hide quantity controls for all products that can be bulk returned
      hideQuantity?: boolean;
    };
    labelCustomText?: { line1?: string; line2?: string };
    refundExpiredLabels?: boolean;
    subscriptionBilling?: BaseSubscriptionBillingSettings;
    labelCombination?: {
      combineLabelsSkuGroups?: {
        enabled: boolean;
        csvFileUrl?: string;
        uploadedAt?: Date;
      };
      onlyCombineLabelsFromSameFulfillment?: boolean;
    };
    applyRestockingFeeToShopifyReturns?: boolean;
    /**
     * Maximum price (in USD) for shipping labels when merchant is liable for the cost.
     */
    merchantMaxLabelPrice?: number;
    /**
     * Maximum price (in USD) that Redo will cover for return labels when Redo is the payer.
     */
    redoMaxLabelPrice?: number;
    /** Pass through carrier label adjustments to merchants */
    passThroughLabelAdjustments?: boolean;
    /**
     * Shopify metafield namespace and keys for customs information.
     * These are used to fetch customs data from products and product variants.
     */
    shopifyCustomsMetafields?: {
      descriptionKey?: string;
      manufacturerKey?: string;
      materialKey?: string;
      variantDescriptionKey?: string;
      variantManufacturerKey?: string;
      variantMaterialKey?: string;
    };
    /**
     * Tax IDs used for international shipments customs declarations.
     * These are sent to carriers (e.g., EasyPost) as tax_identifiers on shipments.
     */
    internationalTaxIds?: {
      /** XI EORI number for shipments coming back into Northern Ireland */
      xiEori?: string;
      /** GB EORI number for shipments coming back into Great Britain */
      gbEori?: string;
      /** US Employer Identification Number for shipments into the US */
      usEin?: string;
    };
    /**
     * Settings for requiring signature confirmation on return shipments
     * based on the total value of products being returned.
     */
    requireReturnShippingSignature?: {
      /** Whether signature confirmation is enabled */
      enabled: boolean;
      /** Threshold in USD above which signature is required */
      threshold?: number;
    };
    verifyAddressForLabel?: boolean;
  };
  pos?: PosSettings;
  analytics?: { lastScheduledReturnInsightDate?: string };
  /** allows merchant to change return address before return is approved */
  canEditReturnAddress?: boolean;
  packageProtection?: PackageProtectionSettings;
  support?: SupportSettings;
  customerAccounts?: CustomerAccountsSettings;
  customerWidget?: CustomerWidgetSettings;
  notifyCustomerDefault: boolean;
  merchantPaidEnabledDates?: DateRange[];
  showLabelExpirationDate: boolean;
  modalTextAdjustments: {
    text1?: string;
    title1?: string;
    text2?: string;
    title2?: string;
    text3?: string;
    title3?: string;
  };
  infoModal?: InfoModal;
  returnBarcodeWidth?: number;
  returnBarcodeHeight?: number;
  modalLogo?: string;
  labelPriceAdjustment?: number | null;
  labelPriceExcludeCoverage?: boolean;
  minimumLabelOunces?: number;
  defaultLineItemOunces?: number;
  toggleFontSize?: number;
  checkoutSelector?: string;
  exchangeBannerColor: string;
  exchangeBannerText: string;
  exchangeBannerFont: string;
  exchangeBannerFontColor: string;
  exchangeBannerShowDoneButton?: boolean;
  exchangeBannerCustomCss?: string;
  submitButtonSelector?: string;
  billingSettings: BillingSettings;
  bundleRulesList: BundleRules[];
  bundles: Bundle[];
  expandedBundlesIgnoreArchived?: boolean;
  pickupEnabled: boolean;
  pickupEnabledForHeavyItems?: boolean;
  pickupMaxLabelUpgradeRange?: number;
  pickupMaxCoveredAmountEnabled?: boolean;
  pickupMaxCoveredAmount?: number;
  shopifyPixelId?: string;
  splitPixelEnabled?: boolean;
  createShopifyReturns?: boolean;
  createShopifyReturnTypes: CompensationMethod[];
  createShopifyReturnsForClaimsAndWarranties?: boolean;
  archiveShopifyOrdersAfterReturn?: boolean;
  closeShopifyReturnWhenProcessed?: CloseShopifyReturnOption;
  instantRefundEnabled?: boolean;
  syncBlockingPrefixes?: string;
  blockFulfillmentOrderPrefixes?: string;
  publicapi?: {
    discountsAreFinalSales?: boolean;
    coverageExcludeLocalPickup?: boolean;
    couponedItemsDiscountThreshold?: number;
  };
  emailDomain?: EmailDomainSettings;
  emailFooter?: EmailFooterSettings;
  orderTracking?: IOrderTrackingSettings;
  orderEditing?: OrderEditingSettings;
  returnTracking?: {
    ads?: OrderTrackingAd[];
    upsellProducts?: UpsellProductSection;
    lineItemsSection?: LineItemsSectionSettings;
  };
  // This should be renamed to exchangeAgentBilling, but we need to backfill existing teams first
  returnsAiExchange: ExchangeAgentBillingSettings;
  exchangeAgent: ExchangeAgentMerchantSettings;
  reviews?: {
    enabled: boolean;
    collectionReviewSettings?: {
      smartSwitch: boolean;
      minimumReviews: number;
      enabledCollections: string[];
    };
    response?: {
      moderationSettings?: ModerationSettings;
      incentivesSettings?: {
        enableDiscount: boolean;
        discountEvents?: { type: ReviewDiscountEvent; amount: number }[];
        discountValueType: DiscountValueType;
        customDiscountCode?: string;
        discountExpirationDays?: number;
        minimumPurchaseAmount?: number;
      };
    };
    googleReviews?: {
      enabled: boolean;
      feedUrl: string;
      lastGenerated: Date;
      reviewCount: number;
    };
  };
  outboundLabels: {
    insuranceIncludedThreshold?: number;
    autoApproveClaimsThreshold?: number;
    includeDraftProductsOnSearch?: boolean;
    includeArchivedProductsOnSearch?: boolean;
    salesChannelsToFilter?: string[];
    showEbayIntegrationCard?: boolean;
    packingSlipId?: string;
    showBinLocationInScanTable?: boolean;
    hasAcceptedUpsAgreement?: boolean;
    hasAcceptedFedexAgreement?: boolean;
    hasAcceptedMerchantCarrierFeeTerms?: boolean;
    canAddDirectUspsCarrierAccount?: boolean;
    canAddDirectFedexCarrierAccount?: boolean;
    useGranularPrintQueue?: boolean;
    orderPrefixes?: string[];
    useFulfillmentSyncWorkflow?: boolean;
    showManualAutoMerge?: boolean;
    address?: {
      autoCleanAddresses?: boolean;
      addressOutreachAutomation?: boolean;
      autoPlaceHoldOnAddressVerificationFailure?: boolean;
      addressAutomationsEnabledAt?: Date;
      enableAddressComparison?: boolean;
    };
    newOmsEnabled: boolean;
    buyLabelsWorkflowEnabled?: boolean;
    rateRefreshWorkflowId?: string;
    calculatedFulfillmentDays?: number;
    blockedCarrierAccounts?: string[];
    useAmazonCarrierAccount?: boolean;
    useAustraliaPostCarrierAccount?: boolean;
    wms?: {
      enabled?: boolean;
      pickSession?: {
        scanType?: ScanType;
        defaultOrderCount?: Record<PickSessionStrategy, number>;
        maxItemQuantity?: number;
        stalePickedRelease?: { enabled?: boolean; days?: number };
        maxItemCount?: { enabled?: boolean; limit?: number };
      };
      binNaming?: BinNamingSettings;
      advancedBinNaming?: AdvancedBinNamingSettings;
      binParsing?: BinParsingConfig;
      fees?: {
        monthlySubscriptionFee?: { amount?: number; turnedOnAt?: Date };
      };
      binCoordinateMonitoring?: {
        enabled?: boolean;
        alertThresholdPercent?: number;
      };
      hideManualToteAssignment?: boolean;
      sourceOfTruthEnabled?: boolean;
    };
    useSoapBoxRates?: boolean;
    enabledCarrierAccounts?: {
      directToCarrierIntegrations?: {
        ups: boolean;
        fedex?: boolean;
        usps?: boolean;
        dhl?: boolean;
        canadaPost?: boolean;
      };
      internationalBridge?: { usps?: boolean };
    };
    dhlDirectIntegrationSettings?: {
      pickupId?: string;
      distributionCenter?: string;
      useDefaultForAllLocations?: boolean;
    };
    blockedServiceLevels?: { carrier: Carriers; service: Service }[];
    disabledRedoServiceLevels?: { carrier: Carriers; service: Service }[];
    carrierAccountDisabledServiceLevels?: {
      carrierAccountId: string;
      disabledServiceLevels?: { carrier: Carriers; service: Service }[];
    }[];
    verifyAddresses?: boolean;
    disableKeyboardOnScan?: boolean;
    printOnPackageScan?: boolean;
    warnOnClosedInScan?: boolean;
    scanVerifyPin?: { enabled: boolean; pin: string };
    expensiveLabelPin?: { enabled: boolean; pin: string; threshold: number };
    pinChecks?: {
      enabled: boolean;
      pin?: string;
      manualScan?: { enabled: boolean };
      manualSelectRate?: { enabled: boolean };
      expensiveLabel?: { enabled: boolean; threshold: number };
      voidLabel?: { enabled: boolean };
      reship?: { enabled: boolean };
      reprintLabel?: { enabled: boolean };
      reprintPackingSlip?: { enabled: boolean };
      continueToScanPageOnOutdatedPackingSlip?: { enabled: boolean };
      mergeAfterPackingSlipPrinted?: { enabled: boolean };
      overrideNotReceived?: { enabled: boolean };
    };
    productUpdateSyncFields?: ProductUpdateSyncFields;
    notifyCustomerOnFulfillment?: boolean;
    fulfillmentNotificationReleaseHour?: number; // Hour (0-23) when pending marketplace fulfillment notifications are released in merchant timezone
    table?: { defaultPageSize?: number };
    insuranceRatePercentage?: number;
    publishedAutomationGroups?: string[];
    useDiscountedOrderPriceForCustoms?: boolean;
    sendPickupEmailOnOverride?: boolean;
    showScanNotifications?: boolean;
    printLabelOnAllScanVerified?: boolean;
    originEmail?: string;
    showScanNoteNotifications?: boolean;
    customs?: CustomsSettings;
    weightFormat?: WeightFormat;
    liveViewCountsEnabled?: boolean;
    canUpdateBinsFromTable?: boolean;
    packingSlipPrintedShopifyTag?: { enabled: boolean; tag: string };
    updateMarketplaceStatusWhenPackingSlipPrinted?: boolean;
    learnedPresets?: {
      [LearnedPresetKind.CARRIER_SERVICE_LEVEL]: boolean;
      [LearnedPresetKind.PARCEL]: boolean;
      [LearnedPresetKind.WEIGHT]: boolean;
    };
    suggestMerge?: {
      enabled: boolean;
      mergeConditions?: {
        [FulfillmentGroupMergeMatcherKind.CUSTOMER]: boolean;
        [FulfillmentGroupMergeMatcherKind.SHIPPING_ADDRESS]: boolean;
        [FulfillmentGroupMergeMatcherKind.BILLING_ADDRESS]: boolean;
        [FulfillmentGroupMergeMatcherKind.RECIPIENT]: boolean;
      };
    };
    billing?: {
      test?: boolean;
      autoReload?: { threshold: number; amount: number; floor: number };
      fundingSources?: PaymentMethod[];
      activeFundingSources?: {
        primary?: FundingSourceReference;
        secondary?: FundingSourceReference;
      };
      disabledUntil?: Date;
      lastFailureMessage?: string;
    };
    internalNotifications?: {
      email?: string;
      notifyOnLowFunds?: boolean;
      notifyOnBalancePayment?: boolean;
      notifyOnShippingClaimProcessed?: boolean;
    };
    batchSuggestions?: {
      enabled: boolean;
      statusOption: BatchSuggestionStatusOption;
      requireAddressVerification?: boolean;
      onlyDomesticOrders?: boolean;
      minGroupCount?: number;
    };
    autoMerge?: {
      enabled?: boolean;
      serviceLevelPriority: ServiceLevelPriority;
      refundOtherShippingOptions: boolean;
      addTags?: { enabled: boolean; tags: string[] };
      addNote?: { enabled: boolean; note: string };
      mergeConditions?: {
        [FulfillmentGroupMergeMatcherKind.PACKING_SLIP_UNPRINTED]?: boolean;
        [FulfillmentGroupMergeMatcherKind.PAYMENT_INFORMATION]?: boolean;
        [FulfillmentGroupMergeMatcherKind.BILLING_ADDRESS]?: boolean;
        [FulfillmentGroupMergeMatcherKind.RECIPIENT]?: boolean;
        [FulfillmentGroupMergeMatcherKind.CUSTOMER_NAME]?: boolean;
        [FulfillmentGroupMergeMatcherKind.OPEN_STATUS]?: boolean;
        [FulfillmentGroupMergeMatcherKind.OPEN_OR_PAYMENT_PENDING_STATUS]?: boolean;
        [FulfillmentGroupMergeMatcherKind.CUSTOMER]?: boolean;
        [FulfillmentGroupMergeMatcherKind.ORDER_ID_NOT_MATCH]?: boolean;
      };
      exceptionConditions?: {
        orderTags?: { enabled: boolean; tags: string[] };
        packingSlipPrinted: { enabled: boolean };
        pickListPrinted: { enabled: boolean };
        maxOrderAge?: { enabled: boolean; days: number; hours?: number };
        salesChannel: { enabled: boolean; channels: string[] };
        productNameContainsX: { enabled: boolean; strings: string[] };
        shipmentTags: { enabled: boolean; tags: string[] };
        sku?: { enabled: boolean; skus: string[] };
        hasMissingBins?: { enabled: boolean };
        orderCount?: { enabled: boolean; maxCount: number };
      };
    };
    autoSplit?: {
      enabled?: boolean;
      addTags?: { enabled: boolean; tags: string[] };
      splitIntoIndividualShipments?: boolean;
      splitConditions?: {
        productTags?: { enabled: boolean; tags: string[] };
        productNameContainsX: { enabled: boolean; strings: string[] };
        sku?: { enabled: boolean; skus: string[] };
        skuContainsX?: { enabled: boolean; strings: string[] };
        containsUnreceivedProducts?: { enabled: boolean };
        productNameDoesNotContainX?: { enabled: boolean; strings: string[] };
        skuDoesNotEqual?: { enabled: boolean; skus: string[] };
        productTagsDoNotInclude?: { enabled: boolean; tags: string[] };
        skuDoesNotContainX?: { enabled: boolean; strings: string[] };
        lineItemCustomAttribute?: {
          enabled: boolean;
          conditions: Array<{
            key: string;
            value: string;
            matchType: CustomAttributeMatchType;
          }>;
        };
      };
    };
    /** Whether personalized slips/inserts feature is enabled for this team */
    personalizedSlipsEnabled?: boolean;
    documents?: {
      shippingLabel?: {
        format: PageFormat;
        includePackingSlip: boolean;
        printSlipOnLabelPrinted?: boolean; // if you are printing a label on 4x6 and a slip on 8.5x11, we will print the slips when you print the label if the workstation has the right printers
        printPersonalizedSlipOnLabelPrinted?: boolean; // if you are printing a label, we will also print personalized slips when configured
        includeLogo: boolean;
        includeSkus: boolean;
        includeCustomerId: boolean;
        includeOrderNumber?: boolean;
        includeSecondaryOrderName?: boolean;
        printFast?: boolean;
        includePersonalizedSlip?: boolean;
      };
      packingSlip?: {
        format: PageFormat;
        orientation: PageOrientation;
        includeFulfillmentBarcode: boolean;
        includeLogo: boolean;
        includeImage: boolean;
        includePrice: boolean;
        includeSku: boolean;
        orderBy: ItemOrderBy;
        printFast?: boolean;
      };
      pickList?: {
        format: PageFormat;
        includeImage: boolean;
        orderBy: ItemOrderBy;
        /** @deprecated Use columns instead */
        includeBin?: boolean;
        /** @deprecated Use columns instead */
        hideOrderNumbers?: boolean;
        /** @deprecated Use columns instead */
        includeTote?: boolean;
        columns?: PickListColumn[];
        fontSize?: PickListFontSize;
        imageSize?: PickListImageSize;
      };
      commercialInvoice?: { numCopies: number };
      localPickupLabel?: { enabled: boolean };
    };
    workstations?: Workstation[];
    automationDelaySeconds?: number;
    binNamespace?: string;
    binKey?: string;
    competitiveSpreadSettings?: {
      enabled: boolean;
      beatRateBy: number;
      minSpreadFloor: number;
      enabledCarriers: Carriers[];
    };
    redoCompetitiveSpreadSettings?: {
      enabled: boolean;
      beatRateBy: number;
      minSpreadDifference: number;
      enabledCarriers: Carriers[];
    };
    pointsSettings?: {
      enabled: boolean;
      uspsLabelPointsAwarded: number;
      fedexLabelPointsAwarded: number;
      upsLabelPointsAwarded: number;
      amazonLabelPointsAwarded: number;
      firstMileLabelPointsAwarded: number;
    };
    labelSettings?: {
      includeRedoLogo?: boolean | null;
      firstPageAdditionalOffset?: { x?: number; y?: number };
    };
    fees?: {
      merchantCarrierAccountLabelFee?: {
        amount?: number;
        lastBilledDate?: Date;
        tiktokShippingAmount?: number;
      };
      monthlySubscriptionFee?: {
        amount?: number;
        turnedOnAt?: Date;
        lastBilledDate?: Date;
      };
      chargeCreditCardProcessingFee?: boolean;
    };
    defaultActionButton: FulfillmentGroupActions;
    useNewPrintQueue?: boolean;
    customMetadata?: {
      customDate1?: CustomMetafield;
      customDate2?: CustomMetafield;
      customDate3?: CustomMetafield;
      customString1?: CustomMetafield;
      customString2?: CustomMetafield;
      customString3?: CustomMetafield;
      customNumber1?: CustomMetafield;
      customNumber2?: CustomMetafield;
      customNumber3?: CustomMetafield;
    };
    customAttributes?: {
      customAttributeDate1?: CustomAttribute;
      customAttributeDate2?: CustomAttribute;
      customAttributeDate3?: CustomAttribute;
      customAttributeString1?: CustomAttribute;
      customAttributeString2?: CustomAttribute;
      customAttributeString3?: CustomAttribute;
      customAttributeNumber1?: CustomAttribute;
      customAttributeNumber2?: CustomAttribute;
      customAttributeNumber3?: CustomAttribute;
    };
    productCustomMetadata?: {
      productCustomDate1?: ProductCustomMetafield;
      productCustomDate2?: ProductCustomMetafield;
      productCustomDate3?: ProductCustomMetafield;
      productCustomString1?: ProductCustomMetafield;
      productCustomString2?: ProductCustomMetafield;
      productCustomString3?: ProductCustomMetafield;
      productCustomNumber1?: ProductCustomMetafield;
      productCustomNumber2?: ProductCustomMetafield;
      productCustomNumber3?: ProductCustomMetafield;
    };
    variantCustomMetadata?: {
      variantCustomDate1?: VariantCustomMetafield;
      variantCustomDate2?: VariantCustomMetafield;
      variantCustomDate3?: VariantCustomMetafield;
      variantCustomString1?: VariantCustomMetafield;
      variantCustomString2?: VariantCustomMetafield;
      variantCustomString3?: VariantCustomMetafield;
      variantCustomNumber1?: VariantCustomMetafield;
      variantCustomNumber2?: VariantCustomMetafield;
      variantCustomNumber3?: VariantCustomMetafield;
    };
    degroupItemsByCustomAttributes?: boolean;
    hideUnderscoreCustomAttributes?: boolean;
    dashboardMetrics?: {
      metrics?: Array<{ key: string; displayed: boolean; order: number }>;
    };
    fulfillmentPromiseDays?: number;
    autoPrintPackingSlip?: {
      workstationId?: string;
      enabled?: boolean;
      delayInMinutes?: number;
    };
    forceResyncConcurrency?: number;
  };
  loyaltyEnabled?: boolean;
  recommendations?: RecommendationSettings;
  exchangeRecommendations?: RecommendationSettings;
  marketing?: MarketingSettings;
  recover?: RecoverSettings;
  variantExchangeTitle?: string;
  variantExchangeSubtitle?: string;
  exchangeGroupsExchangeTitle?: string;
  exchangeGroupsExchangeSubtitle?: string;
  sameItemTitle?: string;
  sameItemSubtitle?: string;
  newItemTitle?: string;
  preProtectedOrdersEnabled: boolean;
  preProtectedOrderMinutes?: number;
  _id: string;
  resourceOverride?: LanguageResourceOverride;
  newGlobalSearch?: boolean;
  globalAI?: boolean;
  customerPortalVersion?: CustomerPortalVersion;
  enableNonShopifyClaims?: boolean;
  nonShopifyClaimsCollection?: { id: string; name: string };
  nonShopifyClaimsDisableEditQuantity?: boolean;
  /** @deprecated Use settings.recycling.enabled instead */
  convertRecyclingProducts?: boolean;
  /** @deprecated Use settings.recycling.productId instead */
  recyclingProductId?: string;
  recommerce?: RecommerceSettings;
  hideManualReviewStep?: boolean;
  hideSummaryCard?: boolean;
  hideGiftOption?: boolean;
  disableRefundLineItems: boolean;
  warranties: WarrantiesSettings;
  extendedWarranties?: ExtendedWarrantySettings;
  brandKit: BrandKit;
  /** @deprecated  Use autoProcessGreenReturnTypes instead */
  autoProcessGreenReturns: boolean;
  autoProcessGreenReturnTypes?: ReturnTypeEnum[];
  autoProcessMerchantCreatedGreenReturnTypes?: ReturnTypeEnum[];
  hideFeeInCompensationMethodModal?: boolean;
  email?: { utm?: { campaign?: { prefix?: string } } };
  createMultipleShopifyReturns?: boolean;
  createSeparateShopifyReturnForGreenReturns?: boolean;
  yofi?: YofiSettings;
  perReturnBilling?: PerReturnBillingSettings;
  redoManagedReturns?: redoManagedReturnsSettings;
  reFulfillment?: ReFulfillmentSettings;
  migratedFromTeamIds?: string[];
  localization?: LocalizationSettings;
}

export function isPreProtectedOrdersEnabled({
  preProtectedOrdersEnabled,
  preProtectedOrderMinutes,
}: {
  preProtectedOrdersEnabled: boolean;
  preProtectedOrderMinutes: number | null | undefined;
}): boolean {
  return (
    isPreProtectedOrdersAllowed(preProtectedOrderMinutes) &&
    preProtectedOrdersEnabled
  );
}

/**
 * As of Oct 3, 2024, for pre-protected orders to be allowed to be enabled, someone at Redo must
 * have set the preProtectedOrderMinutes to a value greater than 0. Once preProtectedOrderMinutes
 * is set, the preProtectedOrdersEnabled setting can be used to toggle the feature on and off.
 */
export function isPreProtectedOrdersAllowed(
  preProtectedOrderMinutes: number | null | undefined,
): boolean {
  return !!preProtectedOrderMinutes && preProtectedOrderMinutes > 0;
}

export type UpsellProductSection = {
  source: UpsellProductSource;
  hoverSecondImage?: boolean;
  collectionId?: string;
  collectionName?: string;
  metafield?: { key: string; namespace: string; metaobjectKey?: string };
  metafieldKey?: string;
  text?: string;
  minimumStock?: number;
  productTags?: string[];
  manuallySelectedProducts?: { productId: string; variantId: string }[];
  recommendedProductFilterId?: string;
  imageAspectRatio?: number;
};

export type LineItemsSectionSettings = { imageAspectRatio?: number };

export type FundingSourceReference =
  | { source: "outbound_labels"; id: string }
  | { source: "general"; priority: "primary" | "secondary" | "card" };

export interface OrderTrackingAd {
  imageUrl: string;
  url: string;
  altText: string;
  type: AdLinkType;
  showOnlyNonSubscribed: boolean;
  duplicate?: () => OrderTrackingAd;
}

export const orderTrackingAdEqual: Equal<OrderTrackingAd> =
  objectEqual<OrderTrackingAd>({
    imageUrl: stringEqual,
    url: stringEqual,
    altText: stringEqual,
    type: stringEqual,
    showOnlyNonSubscribed: booleanEqual,
    duplicate: optionalEqual(() => true),
  });

export interface HideRedoConfig {
  enabled: boolean;
  productSelector?: string;
  checkoutButtonSelector?: string;
  cartCountModifications?: CartCountModification[];
  cartPriceModifications?: CartCountModification[];
  cartDiscountModifications?: CartCountModification[];
}

export interface YofiSettings {
  enabled: boolean;
  paymentsEnabled: boolean;
  percentage: number;
  autoFlagReturns: boolean;
}

export interface CartCountModification {
  selector: string;
  search: string;
  replace: string;
}

export interface ModerationSettings {
  autoPublish: boolean;
  ratingLevel?: number;
  blockUnverifiedReviews: boolean;
  blockMediaReviews: boolean;
  blockProfanityReviews: boolean;
  blockNegativeSentimentReviews: boolean;
  publishAfterTime?: number;
  excludeReturnReviewers: boolean;
  excludedCustomerTags?: string[];
  excludedProducts?: string[];
  reviewPrioritization?: ReviewPrioritizationOption;
}

export interface ToggleFields {
  titleText: string;
  positiveSubtext: string;
  negativeSubtext: string;
}

export interface CollectionInfo {
  id: string;
  name: string;
  kind: "smart" | "custom";
}

export type ExchangeGroupType =
  | "tag:tag"
  | "tag:collection"
  | "name:collection"
  | "collection:collection";

export type PriceDifferenceType = "ignore" | "apply";

export interface BaseExchangeGroup {
  type: ExchangeGroupType;
  groupName: string;
  priceDifference: PriceDifferenceType;
}

export interface TagForTagGroup extends BaseExchangeGroup {
  type: "tag:tag";
  sourceTags: string[];
  targetTags: string[];
}

export interface NameForCollectionGroup extends BaseExchangeGroup {
  type: "name:collection";
  sourceName: string;
  targetCollection: CollectionInfo;
}

export interface TagForCollectionGroup extends BaseExchangeGroup {
  type: "tag:collection";
  sourceTags: string[];
  targetCollection: CollectionInfo;
}

export interface CollectionForCollectionGroup extends BaseExchangeGroup {
  type: "collection:collection";
  sourceCollection: CollectionInfo;
  targetCollection: CollectionInfo;
}
export const CollectionInfoZodSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: createEnum(["smart", "custom"], { enumName: "CollectionKind" }),
});
export const ExchangeGroupType = [
  "tag:tag",
  "tag:collection",
  "name:collection",
  "collection:collection",
] as const;

export const ExchangeGroupTypeZodSchema = createEnum(ExchangeGroupType, {
  enumName: "ExchangeGroupType",
});

export const PriceDifferenceType = ["ignore", "apply"] as const;
export const PriceDifferenceTypeZodSchema = createEnum(PriceDifferenceType, {
  enumName: "PriceDifferenceType",
});
export const BaseExchangeGroupZodSchema = z.object({
  type: ExchangeGroupTypeZodSchema,
  groupName: z.string(),
  priceDifference: PriceDifferenceTypeZodSchema,
});

export const TagForTagGroupZodSchema = BaseExchangeGroupZodSchema.extend({
  type: z.literal("tag:tag"),
  sourceTags: z.array(z.string()),
  targetTags: z.array(z.string()),
});

export const NameForCollectionGroupZodSchema =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("name:collection"),
    sourceName: z.string(),
    targetCollection: CollectionInfoZodSchema,
  });

export const TagForCollectionGroupZodSchema = BaseExchangeGroupZodSchema.extend(
  {
    type: z.literal("tag:collection"),
    sourceTags: z.array(z.string()),
    targetCollection: CollectionInfoZodSchema,
  },
);

export const CollectionForCollectionGroupZodSchema =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("collection:collection"),
    sourceCollection: CollectionInfoZodSchema,
    targetCollection: CollectionInfoZodSchema,
  });

export const ExchangeGroupZodSchema = z.union([
  TagForTagGroupZodSchema,
  NameForCollectionGroupZodSchema,
  TagForCollectionGroupZodSchema,
  CollectionForCollectionGroupZodSchema,
]);

// If you update this, make sure to update the ExchangeGroupZodSchema
// Use ExchangeGroupZodSchema over this when possible
export type ExchangeGroup =
  | TagForTagGroup
  | NameForCollectionGroup
  | TagForCollectionGroup
  | CollectionForCollectionGroup;

export const exchangeGroupEqual: Equal<ExchangeGroup> =
  objectEqual<ExchangeGroup>({
    type: stringEqual,
    groupName: stringEqual,
    priceDifference: stringEqual,
    sourceName: optionalEqual(stringEqual),
    sourceTags: optionalEqual(arrayEqual(stringEqual)),
    targetTags: optionalEqual(arrayEqual(stringEqual)),
    sourceCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
    targetCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
  });

export enum AdvancedExchanges {
  ENABLED = "all",
  /** Limits advanced exchanges to items with value less than or equal to the exchange value */
  LESS_THAN_EQUAL = "less_than_equal",
  DISABLED = "variant_only",
}

export type Onboarding = any;

export interface CustomerSupportSetting {
  ask: boolean;
  ifYes: string;
  then: string;
}

export interface Provinces {
  country: CountryCode;
  provinceCodes: string[];
}

export interface ShippingLocation {
  _id?: string;
  flowTypes: string[];
  condition: LocationCondition | null;
  multipleChoiceQuestion: string | null;
  multipleChoiceQuestionAnswers: string[] | null;
  returnReasons: string[] | null;
  originalLocation: boolean;
  address?: Address;
  locationId: string;
  restockExternalLocationId?: string | null;
  tags: string[] | null;
  collections: string[] | null;
  discounts: string[] | null;
  priceMatchType: PriceOption | null;
  price: string | null;
  skuMatchType: SkuOption | null;
  skus: string[] | null;
  salesChannels: string[] | null;
  countries: string[] | null;
  provinces: Provinces | null;
  vendors: string[] | null;
  variantMetafieldInfo?: {
    namespace: string;
    key: string;
    comparison: MetafieldMatchType;
    value: string;
  } | null;
  trackstarWarehouseId?: string | null;
  blueyonderLocationId?: string | null;
  inmarLocationId?: string | null;
  isSekoHub?: boolean | null;
  aims360WarehouseId?: string | null;
  externalReturnHub?: {
    provider: ExternalReturnHubType;
    locationId?: string | null;
  } | null;
  returnsCenterId?: string | null;
}

export interface TeamUser {
  roles?: string[];
  _id: string;
  user: string | GetUser;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export interface RenderedTeamUser {
  roles?: string[];
  _id: string;
  user: GetUser;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export type RenderedTeam = Omit<Team, "users"> & { users: RenderedTeamUser[] };

export type RefundType = "store_credit" | "refund";

export enum SenderEmailSetupStatus {
  CONNECTED = "connected",
  UNVERIFIED = "unverified",
}

export interface SenderEmail {
  email: string;
  emailSignature?: string | null;
  name: string;
  postmarkId: number;
  domainId: number;
  dkimHost: string;
  dkimText: string;
  returnPathDomain: string;
  returnPathValue: string;
  setupStatus?: SenderEmailSetupStatus | null;
}

export interface BillingSettings {
  schedule: "bimonthly" | "weekly" | "monthly";
  dayOfWeek?: number;
  firstBillingDayOfMonth?: number;
  /** Day of month for monthly billing (1-28) */
  billingDayOfMonth?: number | null;
  chargeProcessingFee: boolean;
}

interface CustomerAccountsSettings {
  enabled?: boolean;
  webPixelEnabled?: boolean;
  // Manual DB setting, no UI. One off request for merchant with new Swym contract
  // for wishlists.
  hideLists?: boolean;
  adBlocks?: AdBlock[];
  saveForLaterPanel?: {
    enabled: boolean;
    title?: string | undefined;
    message?: string | undefined;
    numViewedProductsUntilPanelOpen?: number | undefined;
    anchorLocation?: SaveForLaterSlideInPanelAnchorLocation | undefined;
  };
  nativeAccounts?: {
    overrideAccountLinks?: boolean | undefined;
    redirectFromAccountsPages?: boolean | undefined;
  };
}

export enum TnplAcceptableRiskLevel {
  NONE = "none",
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  ANY = "any",
}

export const TnplOrderRiskHierarchy = [
  TnplAcceptableRiskLevel.NONE,
  TnplAcceptableRiskLevel.LOW,
  TnplAcceptableRiskLevel.MEDIUM,
  TnplAcceptableRiskLevel.HIGH,
];

export enum TnplShippingPolicyType {
  REGULAR = "regular",
  CUSTOM = "custom",
}

export interface TnplShippingPolicy {
  type: TnplShippingPolicyType;
  customDeliveryProfileId?: string;
}

export const ShopifyRiskToTnplRisk: Record<
  RiskAssessmentResult,
  TnplAcceptableRiskLevel
> = {
  [RiskAssessmentResult.None]: TnplAcceptableRiskLevel.NONE,
  [RiskAssessmentResult.Low]: TnplAcceptableRiskLevel.LOW,
  [RiskAssessmentResult.Medium]: TnplAcceptableRiskLevel.MEDIUM,
  [RiskAssessmentResult.High]: TnplAcceptableRiskLevel.HIGH,
  [RiskAssessmentResult.Pending]: TnplAcceptableRiskLevel.ANY, // Any risk level can also be acceptable if we don't have a risk assessment at all
};

export interface TnplSettings {
  enabled: boolean;
  widget?: TnplWidgetSettings;
  acceptableRiskLevel: TnplAcceptableRiskLevel;
  useMinutesForTrialPeriod?: boolean;
}

export interface TnplWidgetSettings {
  clickOriginalAddToCartButton: boolean;
  enabled: boolean;
  infoModal?: InfoModal;
  button: TryFirstButtonUiTheme;
  customCss?: string;
  placement?: {
    button: { selector: string; placementType: InsertionPosition };
  };
}

export interface TryFirstButtonUiTheme {
  text?: string;
  textColor?: string;
  bgColor?: string;
  borderColor?: string;
  subtext?: string;
  subtextColor?: string;
  subtextFontSize?: string;
}

export interface EmailSignUpSettings {
  incentivize: boolean;
  buttonLabel: string;
  signUpDiscountAmount: string;
}

export interface ConciergeSkills {
  backInStockNotifications?: { enabled: boolean };
  orderTracking?: { enabled: boolean };
  cartEditing?: { enabled: boolean };
  createDiscountCode?: {
    enabled: boolean;
    maxDiscountPercentage?: number;
    discountCodeExpirationMinutes?: number;
  };
  productBundle?: {
    enabled: boolean;
    maxDiscountPercentage?: number;
    discountCodeExpirationMinutes?: number;
  };
  webScrapingForProductInformation?: { enabled: boolean };
  forceUseRetrieveSupportKnowledge?: { enabled: boolean };
  returnsPortalLinkOverride?: { url: string };
  addOrderDiscount?: {
    enabled: boolean;
    addOrderDiscountTimeWindowAfterPlacingMinutes?: number;
  };
  addOrderGiftNote?: {
    enabled: boolean;
    addOrderGiftNoteTimeWindowAfterPlacingMinutes?: number;
  };
}

interface ConciergeSettings {
  enabled: boolean;
  placeProductFormFromThemeExtension?: boolean;
  themeExtensionProductFormCustomization?: {
    placeholder?: string;
    buttonText?: string;
    fontSize?: string;
    borderRadius?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
  };
  themeExtensionProductFormCustomCss?: string;
  guardrails?: { storeContactInformation?: string };
  ticketHandoffSettings?: {
    handoffType?: string;
    emailHandoffMessage?: string;
    forwardToExternalEmail?: boolean;
    collectCustomerEmailBeforeHandoff?: boolean;
  };
  general?: {
    name?: string;
    welcomeMessage?: string;
    toneOfVoice?: string;
    brandDescription?: string;
    customerDescription?: string;
    outputLength?: ResponseLength;
  };
  context?: { productMetafieldKeys?: string[] };
  questionPromptCount?: number;
  skills?: ConciergeSkills;
  emailSignUp?: EmailSignUpSettings | undefined;
  proactiveChipEngagement?: boolean;
  overriddenBestSellers?: string[];
  narrowProductRecommendation?: { enabled: boolean; productCount: number };
}

interface CustomerWidgetSettings {
  general?: {
    chips?: {
      selectedPredefinedChips: PredefinedChipOptions[];
      customChips: string[];
    };
    customCss?: string;
    openToWelcomePage?: boolean;
    enableInAppEmbed?: boolean;
    widgetQuickActions?: QuickAction[];
    widgetQuickActionFontSize?: string;
    selfServe?: {
      startReturnEnabled: boolean;
      startReturnLabel: string;
      startClaimEnabled: boolean;
      editOrderLabel: string;
      trackPackageEnabled: boolean;
      trackPackageSettingLabel: string;
    };
    showCustomerWidgetInReturnApp?: boolean;
    mainHeadingText?: string;
    homeHeadingText?: string;
    headingLogoUrl?: string;
    hideButton?: boolean;
    hideWidgetAfterHours?: boolean;
    hideWidgetOnProductPages?: boolean;
    widgetFontFamily?: string;
    widgetMessageBubbleFontSize?: string;
    widgetHeaderFontSize?: string;
    widgetButtonFontSize?: string;
    chatWithUsButtonText?: string;
    widgetColor?: string;
    widgetSecondaryColor?: string;
    widgetSecondaryColorEnabled?: boolean;
    widgetPosition?: "left" | "right";
    xAdjustment?: string;
    yAdjustment?: string;
    mobileXAdjustment?: string;
    mobileYAdjustment?: string;
    bubbleButtonText?: string;
    gradientSetting?: { gradientStrength?: number; inverted?: boolean };
    widgetHoursEnabled?: boolean;
    widgetHoursTimezone?: string;
    widgetHours?: {
      sunday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      monday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      tuesday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      wednesday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      thursday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      friday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
      saturday?: {
        enabled?: boolean | null;
        timeBlocks?: { start?: string | null; end?: string | null }[];
      };
    };
    mobileFullscreen?: boolean;
  };
  support?: {
    chatExpirationMinutes?: number;
    chatExpirationMessage?: string;
    unresponsiveConversionMinutes?: number;
    unresponsiveConversionSeconds?: number;
    unresponsiveConversionEnabled?: boolean;
    emailConversionPrepMessage?: string;
    liveChatConversionPrepMessage?: string;
    hideSupportWidgetFeatures?: boolean;
    messagingHeadingText?: string;
  };
  customerAccounts?: {
    registerHeader?: string;
    registerSubtext?: string;
    accountTabSections?: AccountTabSection[];
    showAccountsPage?: boolean;
  };
}

interface DayHours {
  enabled?: boolean | undefined;
  timeBlocks?: { start?: string | undefined; end?: string | undefined }[];
}

export interface WidgetHours {
  sunday?: DayHours | undefined;
  monday?: DayHours | undefined;
  tuesday?: DayHours | undefined;
  wednesday?: DayHours | undefined;
  thursday?: DayHours | undefined;
  friday?: DayHours | undefined;
  saturday?: DayHours | undefined;
}

export interface TeamInfo {
  id: string;
  widgetSlug: string;
  returnsEnabled: boolean;
  customerClaimsEnabled: boolean;
  orderTrackingEnabled: boolean;
  marketingEnabled: boolean;
  marketingSubscribeFromLogin: boolean;
  customerAccountsEnabled: boolean;
  conciergeEnabled: boolean;
  conciergeLiveChat: boolean;
  conciergeSettings: {
    general: { assistantName: string | undefined; welcomeMessage: string };
    emailSignUp?: EmailSignUpSettings | undefined;
    proactiveChipEngagement: boolean;
    ticketHandoffSettings?: {
      handoffType?: string;
      emailHandoffMessage?: string;
      forwardToExternalEmail?: boolean;
      collectCustomerEmailBeforeHandoff?: boolean;
    };
    skills?: SupportAgentSkills;
    useOmsEventsForOrderEditing?: boolean;
    tryItOnMessage?: string;
  };
  chips: {
    selectedPredefinedChips: PredefinedChipOptions[];
    customChips: string[];
  };
  accountTab: {
    registerHeader: string;
    registerSubtext: string;
    sections: AccountTabSection[];
  };
  showAccountsPage?: boolean;
  overrideAccountLinks: boolean;
  redirectFromAccountsPages: boolean;
  hideButton: boolean;
  multipassAvailable: boolean;
  supportFeaturesEnabled: boolean;
  url: string;
  chatHeadingLogoUrl: string;
  storeUrl: string;
  customerPortalDomain: string;
  customerPortalPathPrefix: string;
  storefrontAccessToken: string;
  customerPortalButtonText?: string;
  returnButtonText: string;
  claimButtonText: string;
  messagingHeadingText: string;
  mainHeadingText: string;
  homeHeadingText: string;
  contactFormSettings?: ContactFormSettings;
  name: string;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  chatFlow: Json;
  widgetCustomCss?: string;
  widgetQuickActions?: QuickAction[];
  widgetQuickActionFontSize?: string;
  csat: { enabled: boolean; surveyMethods: string[]; allowRemarks: boolean };
  selfServe: {
    startReturnEnabled: boolean;
    startReturnLabel: string;
    startClaimEnabled: boolean;
    editOrderEnabled: boolean;
    editOrderLabel: string;
    trackPackageEnabled: boolean;
    trackPackageSettingLabel: string;
  };
  hideWidgetAfterHours?: boolean;
  hideWidgetOnProductPages?: boolean;
  widgetPrimaryColor?: string;
  widgetSecondaryColorEnabled?: boolean;
  widgetSecondaryColor?: string;
  widgetFontFamily?: string;
  widgetMessageBubbleFontSize?: string;
  widgetHeaderFontSize?: string;
  widgetButtonFontSize?: string;
  chatWithUsButtonText?: string;
  widgetPosition?: "left" | "right";
  xAdjustment?: string;
  yAdjustment?: string;
  mobileXAdjustment?: string;
  mobileYAdjustment?: string;
  bubbleButtonText?: string;
  gradientSetting?: { gradientStrength?: number; inverted?: boolean };
  widgetHoursTimezone?: string;
  widgetHoursEnabled?: boolean;
  widgetHours?: WidgetHours | undefined;
  openToWelcomePage?: boolean;
  mobileFullscreen?: boolean;
}

export interface Workstation {
  _id?: string;
  name: string;
  overridePackingSlipTemplateId?: string;
  defaultPresetId?: string;
  selectedPrinters: {
    format: PageFormat;
    printerId: number;
    printerName: string;
    printerOptions?: PrinterOptions;
    isPremium?: boolean;
  }[];
}

export class OrderTrackingSettings {
  constructor(
    private readonly teamSettings: Pick<
      MarketingTeam["settings"],
      "orderTracking" | "shopifySubscription" | "marketing"
    >,
  ) {}

  withinUsageCap() {
    if (!this.teamSettings.orderTracking?.billing?.enabled) {
      return true;
    }
    const pricingDetails = getUsagePlanPricing(
      this.teamSettings.shopifySubscription?.[0],
    );
    const balanceUsed = Number(pricingDetails?.balanceUsed?.amount ?? 0);
    const cappedAmount = Number(pricingDetails?.cappedAmount?.amount ?? 0);
    return balanceUsed < cappedAmount;
  }

  servicesActive() {
    if (!this.teamSettings?.orderTracking?.enabled) {
      return false;
    }

    if (this.teamSettings.orderTracking.billing?.rerouteBillingToMarketing) {
      return true;
    }

    if (isInvoiceBillingActive(this.teamSettings.orderTracking.billing)) {
      /**
       * Intentionally not checking payment method status. The billing engine
       * system bills only once at the start/end of a cycle rather than at every
       * usage event, so it's less critical that the payment method be valid the
       * whole time we're sending.
       */
      return true;
    }

    const usagePricing = getUsagePlanPricing(
      this.teamSettings.shopifySubscription?.[0],
    );

    return (
      (usagePricing && this.withinUsageCap()) ||
      this.teamSettings?.orderTracking?.billing?.continueIfNotAccepted
    );
  }

  canSendTexts() {
    if (this.teamSettings?.orderTracking?.billing?.rerouteBillingToMarketing) {
      return this.teamSettings?.marketing?.textsEnabled;
    }
    return this.teamSettings?.orderTracking?.smsEnabled;
  }
}

export const teamCanUseAi = (team?: Team) => {
  const ai = team?.settings?.support?.ai;
  const billing = team?.settings?.support?.billing?.ai;
  if (ai && billing) {
    const billingConfirmed =
      team.settings.support?.billing?.ai?.billingAccepted;
    const atShopifyCap = billing.atShopifyCap;
    const lastDayActive = billing.lastDayActive;
    const lastDayActiveIsInFuture = lastDayActive
      ? new Date(lastDayActive) > new Date()
      : false;
    const freeTrialEndDate = billing.freeTrialEndDate;
    const inFreeTrial = freeTrialEndDate
      ? new Date(freeTrialEndDate) > new Date()
      : false;

    return (
      ai.enabled &&
      (billingConfirmed || lastDayActiveIsInFuture || inFreeTrial) &&
      !atShopifyCap
    );
  }
  return false;
};

export const allowSupportAiCopilot = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.copilotEnabled ?? true;
};

export const shouldAutoGenerateAiResponses = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.autoGenerateResponses ?? false;
};

export const shouldUseTeamEmailSignature = (team?: Team): boolean => {
  return team?.settings?.support?.ai?.useEmailSignature ?? false;
};

export const allowSupportAutopilot = (team?: Team): boolean => {
  const allowed = teamCanUseAi(team);
  return (allowed && team?.settings?.support?.ai?.autopilotEnabled) ?? false;
};

export function isSupportFeaturesEnabled({
  isSupportEnabled,
  hideSupportWidgetFeatures,
}: {
  isSupportEnabled: boolean;
  hideSupportWidgetFeatures: boolean;
}): boolean {
  return isSupportEnabled && !hideSupportWidgetFeatures;
}

export function isConciergeLiveChatEnabled({
  isConciergeEnabled,
  automateLiveChat,
}: {
  isConciergeEnabled: boolean;
  automateLiveChat: boolean;
}): boolean {
  return isConciergeEnabled && automateLiveChat;
}

export function getChatHeadingLogoUrl({
  widgetHeadingLogoUrl,
  brandKitFaviconUrl,
  portalFaviconUrl,
}: {
  widgetHeadingLogoUrl: string | undefined;
  brandKitFaviconUrl: string | undefined;
  portalFaviconUrl: string | undefined;
}): string {
  return widgetHeadingLogoUrl || brandKitFaviconUrl || portalFaviconUrl || "";
}

export function isConciergeChatWithTeamMemberEnabled(
  supportFeaturesEnabled: boolean,
  isWithinHours: boolean,
): boolean {
  return supportFeaturesEnabled && isWithinHours;
}

/**
 * @deprecated Use team.settings.coverageEnabled does not represent returns OR
 * final sale returns like it did before, now it is only returns coverage
 */
export function returnsBaseCoverageIsEnabled(returnCoverage: {
  exchange: boolean;
  refund: boolean;
  credit: boolean;
}) {
  return (
    returnCoverage.exchange || returnCoverage.refund || returnCoverage.credit
  );
}

export function returnsCoverageIsEnabled(
  returnCoverage: { exchange: boolean; refund: boolean; credit: boolean },
  returnsGeneral: boolean,
) {
  return returnsBaseCoverageIsEnabled(returnCoverage) && returnsGeneral;
}

export function shouldAutoProcessGreenReturnType({
  returnType,
  autoProcessGreenReturnTypes,
  autoProcessGreenReturns,
}: {
  returnType: ReturnTypeEnum;
  autoProcessGreenReturnTypes: ReturnTypeEnum[] | null | undefined;
  autoProcessGreenReturns: boolean | null | undefined;
}): boolean {
  return (
    autoProcessGreenReturnTypes?.includes(returnType) ||
    !!autoProcessGreenReturns
  );
}

export function shouldAutoProcessMerchantCreatedGreenReturnType({
  returnType,
  autoProcessMerchantCreatedGreenReturnTypes,
}: {
  returnType: ReturnTypeEnum;
  autoProcessMerchantCreatedGreenReturnTypes:
    | ReturnTypeEnum[]
    | null
    | undefined;
}): boolean {
  return !!autoProcessMerchantCreatedGreenReturnTypes?.includes(returnType);
}

// This function returns the first payment method that should be attempted.
// If there is no primary payment method, it will return a secondary one, if available.
export function teamGetPaymentMethod(
  stripe: Team["stripe"],
): PaymentMethod | null {
  if (isStripePaymentMethod(stripe?.paymentMethod)) {
    return stripe.paymentMethod;
  } else if (isStripePaymentMethod(stripe?.secondaryPaymentMethod)) {
    return stripe.secondaryPaymentMethod;
  } else if (stripe?.card?.id) {
    return { legacyCard: stripe.card };
  }
  return null;
}

// This function returns an alternate payment method, if available.
// If there is no primary method, it is assumed that the secondary
// method has already been tried and will return null.
export function teamGetAlternatePaymentMethod(
  stripe: Team["stripe"],
): PaymentMethod | null {
  if (!isStripePaymentMethod(stripe?.paymentMethod)) {
    return null;
  }
  if (isStripePaymentMethod(stripe?.secondaryPaymentMethod)) {
    return stripe.secondaryPaymentMethod;
  }
  if (stripe?.card?.id) {
    return { legacyCard: stripe.card };
  }
  return null;
}

export type MerchantBillingMethod = "stripe" | "fake";

export type ShopperFacingBillingInfo = { status: ShopperFacingBillingStatus };

export enum ShopperFacingBillingStatus {
  UP_TO_DATE = "up_to_date",
  MISSING_BILLING_INFO = "missing_billing_info",
  BILLING_FAILURE = "billing_failure",
}

export { ShippingRateTableSchema as ExchangeShippingRateTableZodSchema } from "./integration/redo-in-shipping/shipping-rate-table";
